<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import KnowledgeBuilder from './components/KnowledgeBuilder.vue';
import AbmIntegrationSettings from './components/AbmIntegrationSettings.vue';
import ShopifyIntegrationSettings from './components/ShopifyIntegrationSettings.vue';
import SgpIntegrationSettings from './components/SgpIntegrationSettings.vue';
import SiprovIntegrationSettings from './components/SiprovIntegrationSettings.vue';
import EventsSettings from './components/EventsSettings.vue';

const store = useStore();
const { t, locale } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const activeTabIndex = ref(0);

const confirmDialog = ref(null);
const confirmTitle = ref('');
const confirmDescription = ref('');

const tabs = computed(() => {
  const baseTabs = [
    { label: t('LUMERAI_SETTINGS.TABS.PERSONA'), value: 0 },
    { label: t('LUMERAI_SETTINGS.TABS.COMPANY_BASE'), value: 1 },
    { label: t('LUMERAI_SETTINGS.TABS.DOCUMENTS_URLS'), value: 10 },
    { label: t('LUMERAI_SETTINGS.TABS.FAQS'), value: 2 },
    { label: t('LUMERAI_SETTINGS.TABS.EVENTS'), value: 21 },
    { label: t('LUMERAI_SETTINGS.TABS.WEBHOOKS'), value: 3 },
    { label: t('LUMERAI_SETTINGS.TABS.PAYMENTS'), value: 17 },
    { label: t('LUMERAI_SETTINGS.TABS.TRANSFERS'), value: 14 },
    { label: t('LUMERAI_SETTINGS.TABS.PIPELINE'), value: 15 },
    { label: t('LUMERAI_SETTINGS.TABS.CALENDAR'), value: 16 },
    { label: t('LUMERAI_SETTINGS.TABS.STICKERS'), value: 12 },
  ];

  baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.INBOXES'), value: 4 });
  baseTabs.push({ label: 'Ligações', value: 20 });
  // eslint-disable-next-line no-use-before-define
  if (sgpIntegrationAvailable.value) {
    baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.SGP'), value: 6 });
  }
  // eslint-disable-next-line no-use-before-define
  if (abmIntegrationAvailable.value) {
    baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.ABM'), value: 13 });
  }
  // eslint-disable-next-line no-use-before-define
  if (shopifyIntegrationAvailable.value) {
    baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.STORE'), value: 18 });
  }
  // eslint-disable-next-line no-use-before-define
  if (siprovIntegrationAvailable.value) {
    baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.SIPROV'), value: 19 });
  }
  baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.PLAYGROUND'), value: 5 });
  baseTabs.push({ label: t('LUMERAI_SETTINGS.TABS.BASE_BUILDER'), value: 11 });

  return baseTabs;
});

const activeTabArrayIndex = computed(() => {
  const index = tabs.value.findIndex(tab => tab.value === activeTabIndex.value);
  return index >= 0 ? index : 0;
});

const isUpdating = ref(false);
const isSavingAbmUsage = ref(false);
const isSavingSgpUsage = ref(false);
const isSavingShopifyUsage = ref(false);
const isSavingSiprovUsage = ref(false);

const aiPromptPersona = ref('');
const aiKnowledgeBase = ref('');
const aiFaqs = ref([]);
const aiAutopilotInboxes = ref([]);
const lumeraiVoiceCallInboxes = ref([]);
const lumeraiVoiceCallCapabilities = ref([]);
const savingVoiceCallCapabilities = ref([]);
const lumeraiVoiceTranscriptsEnabled = ref(false);
const voiceCallCapabilityOptions = [
  {
    key: 'catalog',
    label: 'Catálogo, produtos e preços',
    description:
      'Consultar peças, variantes e orçamento no catálogo conectado.',
  },
  {
    key: 'orders',
    label: 'Pedidos e entregas',
    description: 'Consultar pedidos, detalhes e andamento da entrega.',
  },
  {
    key: 'integrations',
    label: 'ERP e sistemas empresariais',
    description:
      'Consultar dados e serviços nos ERPs habilitados para a conta.',
  },
  {
    key: 'calendar',
    label: 'Agenda',
    description:
      'Consultar horários e gerenciar compromissos nos calendários conectados.',
  },
  {
    key: 'coverage',
    label: 'Locais e cobertura',
    description:
      'Verificar disponibilidade de atendimento ou entrega por endereço.',
  },
  {
    key: 'custom_integrations',
    label: 'Integrações personalizadas',
    description: 'Usar as integrações/webhooks configurados para a LumerAI.',
  },
  {
    key: 'actions',
    label: 'Criar ou alterar dados',
    description:
      'Autorizar ações como criar chamados, alterar serviços ou preparar checkout.',
  },
];
const defaultVoiceCallCapabilities = voiceCallCapabilityOptions.map(
  option => option.key
);
const aiAutopilotDebounceSeconds = ref(15);
const defaultPaymentPolicy = () => ({
  enabled: false,
  online_only_notice: true,
  credit_card_analysis_hours: 24,
  credit_card_installments: [
    {
      minimum_brl: 300.01,
      installments: 12,
      label: 'compras acima de R$ 300,00',
    },
    {
      minimum_brl: 150,
      installments: 6,
      label: 'compras entre R$ 150,00 e R$ 300,00',
    },
    { minimum_brl: 0, installments: 3, label: 'compras abaixo de R$ 150,00' },
  ],
  boleto: { due_business_days: 2, compensation_business_days: 3 },
  pix: { expiration_minutes: 30, quick_approval: true },
});
const lumeraiPaymentPolicy = ref(defaultPaymentPolicy());
const knowledgeDocuments = ref([]);
const knowledgeDocumentsMeta = ref({});
const knowledgeSourceUrl = ref('');
const knowledgeSourceName = ref('');
const knowledgePdfFile = ref(null);
const knowledgeFileInput = ref(null);
const isLoadingKnowledgeDocuments = ref(false);
const isCreatingKnowledgeDocument = ref(false);

// SGP Settings
const sgpIntegration = ref(null);
const sgpActionPolicyItems = [
  {
    setting: 'sgp_contract_lookup_enabled',
    instructionKey: 'sgp_contract_lookup_enabled',
    title: 'Identificar cliente e contratos',
    description: 'Consulta segura por CPF/CNPJ.',
    placeholder:
      'Ex.: Confirme o nome completo antes de seguir para outro atendimento.',
  },
  {
    setting: 'sgp_billing_enabled',
    instructionKey: 'sgp_billing_enabled',
    title: 'Faturas e segunda via',
    description: 'Consultar boleto ou PIX do contrato.',
    placeholder:
      'Ex.: Envie a segunda via somente para o titular identificado.',
  },
  {
    setting: 'sgp_ticket_enabled',
    instructionKey: 'sgp_ticket_enabled',
    title: 'Abrir chamado técnico',
    description: 'Registrar solicitações de suporte no SGP.',
    placeholder:
      'Ex.: Antes de abrir, confirme o endereço e peça uma descrição do problema.',
  },
  {
    setting: 'sgp_ticket_management_enabled',
    instructionKey: 'sgp_ticket_management_enabled',
    title: 'Atualizar chamados',
    description: 'Adicionar observações ou encerrar OS existente.',
    placeholder:
      'Ex.: Encerramento somente após o cliente confirmar que o serviço voltou.',
  },
  {
    setting: 'sgp_trust_release_enabled',
    instructionKey: 'sgp_trust_release_enabled',
    title: 'Liberação por confiança',
    description: 'Ação financeira sensível: desbloqueio temporário.',
    placeholder:
      'Ex.: Só libere após confirmar a data de pagamento e o aceite explícito do titular.',
  },
  {
    setting: 'sgp_wifi_management_enabled',
    instructionKey: 'sgp_wifi_management_enabled',
    title: 'Gerenciar Wi-Fi',
    description: 'Alterar nome ou senha da rede do roteador.',
    placeholder:
      'Ex.: Solicite os últimos 4 dígitos do CPF antes de alterar a rede.',
  },
  {
    setting: 'sgp_service_password_enabled',
    instructionKey: 'sgp_service_password_enabled',
    title: 'Alterar senha do serviço',
    description: 'Trocar senha PPPoE, TV ou outro serviço.',
    placeholder:
      'Ex.: Exija confirmação explícita do titular imediatamente antes da troca.',
  },
];
const defaultSgpActionInstructions = () =>
  Object.fromEntries(
    sgpActionPolicyItems.map(item => [item.instructionKey, ''])
  );
const parseSgpActionInstructions = value => {
  if (value && typeof value === 'object') {
    return { ...defaultSgpActionInstructions(), ...value };
  }

  try {
    return { ...defaultSgpActionInstructions(), ...JSON.parse(value || '{}') };
  } catch {
    return defaultSgpActionInstructions();
  }
};
const defaultSgpUsage = () => ({
  sgp_contract_lookup_enabled: true,
  sgp_billing_enabled: true,
  sgp_ticket_enabled: true,
  sgp_ticket_management_enabled: true,
  sgp_trust_release_enabled: true,
  sgp_wifi_management_enabled: true,
  sgp_service_password_enabled: true,
  sgp_usage_instruction: '',
});
const sgpUsage = ref(defaultSgpUsage());
const sgpActionInstructions = ref(defaultSgpActionInstructions());
const sgpIntegrationAvailable = computed(() => sgpIntegration.value?.available);

// Siprov settings
const siprovIntegration = ref(null);
const siprovActionPolicyItems = [
  {
    setting: 'siprov_associado_lookup_enabled',
    title: 'Consultar associados',
    description: 'Localizar associados por CPF, CNPJ, placa, chassi ou nome.',
  },
  {
    setting: 'siprov_benefit_lookup_enabled',
    title: 'Consultar benefícios',
    description: 'Consultar benefícios por associado, placa ou cartão.',
  },
  {
    setting: 'siprov_event_lookup_enabled',
    title: 'Consultar eventos',
    description: 'Consultar eventos e protocolos do associado.',
  },
  {
    setting: 'siprov_plan_lookup_enabled',
    title: 'Consultar planos',
    description: 'Listar planos e negociações disponíveis na conta.',
  },
  {
    setting: 'siprov_benefit_items_enabled',
    title: 'Consultar itens do benefício',
    description: 'Detalhar os itens de um benefício já identificado.',
  },
];
const defaultSiprovUsage = () => ({
  siprov_integration_enabled: false,
  siprov_associado_lookup_enabled: true,
  siprov_benefit_lookup_enabled: true,
  siprov_event_lookup_enabled: true,
  siprov_plan_lookup_enabled: true,
  siprov_benefit_items_enabled: true,
  siprov_usage_instruction: '',
});
const siprovUsage = ref(defaultSiprovUsage());
const siprovIntegrationAvailable = computed(
  () => siprovIntegration.value?.available
);

// IXC Settings
const ixcUrl = ref('');
const ixcToken = ref('');

// MK-Auth Settings
const mkauthUrl = ref('');
const mkauthClientId = ref('');
const mkauthClientSecret = ref('');

// Hubsoft Settings
const hubsoftUrl = ref('');
const hubsoftToken = ref('');
const abmIntegration = ref(null);
const abmCatalogEnabled = ref(false);
const abmCatalogResultLimit = ref(3);
const abmCatalogImagesOnly = ref(false);
const abmCatalogUsageInstruction = ref('');
const abmClientLookupUsageInstruction = ref('');
const abmIntegrationAvailable = computed(() => abmIntegration.value?.available);
const shopifyIntegration = ref(null);
const shopifyCatalogResultLimit = ref(10);
const shopifyCatalogImagesOnly = ref(false);
const shopifyMetaCatalogEnabled = ref(false);
const shopifyMetaCatalogId = ref('');
const shopifyMetaCatalogToken = ref('');
const shopifyMetaCatalogTokenConfigured = ref(false);
const shopifyStoreName = ref('');
const shopifyActionPolicyItems = [
  {
    key: 'shopify_order_lookup_enabled',
    instructionKey: 'shopify_order_lookup_enabled',
    title: 'Consultar pedidos',
    description: 'Lista os pedidos vinculados ao contato atual.',
    placeholder:
      'Ex.: Consulte somente quando o cliente perguntar sobre seus pedidos.',
  },
  {
    key: 'shopify_order_details_enabled',
    instructionKey: 'shopify_order_details_enabled',
    title: 'Consultar detalhes do pedido',
    description: 'Exibe itens, valores e status de um pedido do contato atual.',
    placeholder:
      'Ex.: Antes de detalhar, confirme qual pedido o cliente quer consultar.',
  },
  {
    key: 'shopify_delivery_tracking_enabled',
    instructionKey: 'shopify_delivery_tracking_enabled',
    title: 'Consultar entrega e rastreio',
    description:
      'Exibe situação de entrega e rastreio do pedido do contato atual.',
    placeholder:
      'Ex.: Informe apenas o rastreio retornado; não prometa novo prazo.',
  },
  {
    key: 'shopify_catalog_enabled',
    instructionKey: 'shopify_catalog_enabled',
    title: 'Consultar catálogo de produtos',
    description:
      'Busca produtos por nome, marca, categoria ou modelo, em listas de até 10 itens.',
    placeholder:
      'Ex.: Antes de buscar, peça uma categoria, marca, modelo ou nome de produto.',
    defaultEnabled: true,
  },
  {
    key: 'shopify_checkout_enabled',
    instructionKey: 'shopify_checkout_enabled',
    title: 'Gerar checkout seguro',
    description:
      'Após confirmação explícita, cria um carrinho e envia o link oficial da loja. Pagamento e dados sensíveis ficam na página segura da loja.',
    placeholder:
      'Ex.: Gere o checkout somente após o cliente confirmar produto, variante e quantidade.',
    defaultEnabled: false,
  },
];
const defaultShopifyPolicy = () =>
  Object.fromEntries(
    shopifyActionPolicyItems.map(item => [
      item.key,
      item.defaultEnabled !== false,
    ])
  );
const defaultShopifyInstructions = () =>
  Object.fromEntries(
    shopifyActionPolicyItems.map(item => [item.instructionKey, ''])
  );
const shopifyPolicy = ref(defaultShopifyPolicy());
const shopifyActionInstructions = ref(defaultShopifyInstructions());
const shopifyIntegrationAvailable = computed(
  () =>
    shopifyIntegration.value?.available && shopifyIntegration.value?.configured
);

const aiBotName = ref('');
const aiCompanyName = ref('');
const aiCompanyPhone = ref('');
const aiCompanyHours = ref('');
const aiCompanyEmail = ref('');
const aiCompanyInfo = ref('');
const aiWebhooks = ref([]);
const lumeraiTransferRules = ref([]);
const transferTeams = ref([]);
const transferUsers = ref([]);
const lumeraiPipeline = ref(null);
const lumeraiPipelineStages = ref([]);
const isLoadingLumeraiPipeline = ref(false);
const isSavingLumeraiPipeline = ref(false);
const isSavingLumeraiPipelineAutomation = ref(false);
const lumeraiPipelineError = ref('');
const lumeraiCalendars = ref([]);
const isLoadingLumeraiCalendars = ref(false);
const isSavingLumeraiCalendar = ref(null);
const lumeraiSalesPipelineAutomationEnabled = ref(true);
const lumeraiAutoResolveEnabled = ref(false);
const aiBotGender = ref('neutral');
const aiResponseTone = ref('professional');
const aiStickersEnabled = ref(false);
const lumeraiStickers = ref([]);
const isLoadingStickers = ref(false);
const isSavingSticker = ref(false);
const isSavingStickerPermissions = ref(false);
const stickerName = ref('');
const stickerInstruction = ref('');
const stickerFile = ref(null);
const stickerFileInput = ref(null);

const inboxes = computed(() => store.getters['inboxes/getSendableInboxes']);

const normalizeAutopilotDebounceSeconds = value => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 15;
  return Math.min(30, Math.max(0, parsed));
};

// Keep the value readable for operators while accepting digits pasted from
// Meta/WhatsApp. It supports Brazilian local, +55 and toll-free 0800 numbers.
const formatCompanyPhone = value => {
  const raw = String(value ?? '');
  const unformattedDigits = raw.replace(/\D/g, '');
  if (unformattedDigits.startsWith('0800')) {
    const digits = unformattedDigits.slice(0, 11);
    const prefix = digits.slice(0, 4);
    const firstPart = digits.slice(4, 7);
    const lastPart = digits.slice(7, 11);
    return [prefix, firstPart, lastPart].filter(Boolean).join(' ');
  }

  const digits = unformattedDigits.slice(0, 13);
  if (digits.startsWith('55') && digits.length > 11) {
    const local = digits.slice(2);
    const area = local.slice(0, 2);
    const number = local.slice(2);
    if (number.length <= 8) {
      return `+55 (${area}) ${number.slice(0, 4)}${number.length > 4 ? `-${number.slice(4)}` : ''}`;
    }
    return `+55 (${area}) ${number.slice(0, 5)}-${number.slice(5)}`;
  }
  if (digits.length <= 2) return digits ? `(${digits}` : '';
  const area = digits.slice(0, 2);
  const number = digits.slice(2);
  if (number.length <= 8) {
    return `(${area}) ${number.slice(0, 4)}${number.length > 4 ? `-${number.slice(4)}` : ''}`;
  }
  return `(${area}) ${number.slice(0, 5)}-${number.slice(5)}`;
};

const onCompanyPhoneInput = value => {
  aiCompanyPhone.value = formatCompanyPhone(value);
};

// Playground State
const playgroundInput = ref('');
const playgroundContactPhone = ref('');
const playgroundContactEmail = ref('');
const playgroundMessages = ref([
  { role: 'ai', content: t('LUMERAI_SETTINGS.PLAYGROUND.GREETING') },
]);
const isSimulating = ref(false);
const voiceCallActive = ref(false);
const voiceCallStatus = ref('listening');
const realtimeVoiceOptions = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
  'marin',
  'cedar',
];
const lumeraiRealtimeVoice = ref('marin');
const cleanRealtimeTranscript = transcript =>
  transcript
    .replace(/<\/?ABM_[A-Z_]+>/gi, '')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
let voicePeerConnection;
let voiceDataChannel;
let voiceMediaStream;
let voiceSessionToken;
let voiceRemoteAudio;
let voiceStartGeneration = 0;
let sendPlaygroundMessage;
let stopVoicePlayground;
let pendingRealtimeToolCalls = {};

const executeRealtimeToolCalls = async calls => {
  const generation = voiceStartGeneration;
  const sessionToken = voiceSessionToken;
  const accountId = currentAccount.value.id;
  if (!sessionToken || voiceDataChannel?.readyState !== 'open') return;

  const outputs = await Promise.all(
    calls.map(async toolCall => {
      try {
        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/lumerai_playground/execute_voice_tool`,
          {
            session_token: sessionToken,
            function_call_id: toolCall.call_id,
            name: toolCall.name,
            arguments: toolCall.arguments,
            contact_phone: playgroundContactPhone.value,
            contact_email: playgroundContactEmail.value,
          }
        );
        return { call_id: toolCall.call_id, output: response.data.output };
      } catch (error) {
        return {
          call_id: toolCall.call_id,
          output: JSON.stringify({
            status: 'error',
            message:
              'A integração falhou. Informe isso sem inventar resultados.',
          }),
        };
      }
    })
  );

  if (
    generation !== voiceStartGeneration ||
    !voiceCallActive.value ||
    voiceDataChannel?.readyState !== 'open'
  ) {
    return;
  }

  outputs.forEach(result => {
    voiceDataChannel.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: result.call_id,
          output: result.output,
        },
      })
    );
  });
  voiceDataChannel.send(
    JSON.stringify({
      type: 'response.create',
      response: { output_modalities: ['audio'] },
    })
  );
  voiceCallStatus.value = 'processing';
};

const voiceCallStatusLabel = computed(() => {
  if (voiceCallStatus.value === 'processing') {
    return t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_STATUS.PROCESSING');
  }
  if (voiceCallStatus.value === 'speaking') {
    return t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_STATUS.SPEAKING');
  }
  return t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_STATUS.LISTENING');
});
const voiceCallButtonLabel = computed(() => {
  if (voiceCallActive.value) {
    return t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_END');
  }
  return t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_START');
});

const playgroundPreviewAttributes = () => ({
  ai_prompt_persona: aiPromptPersona.value,
  ai_knowledge_base: aiKnowledgeBase.value,
  ai_bot_name: aiBotName.value,
  ai_bot_gender: aiBotGender.value,
  ai_response_tone: aiResponseTone.value,
  ai_company_name: aiCompanyName.value,
  ai_company_phone: aiCompanyPhone.value,
  ai_company_email: aiCompanyEmail.value,
  ai_company_hours: aiCompanyHours.value,
  ai_company_info: aiCompanyInfo.value,
  ai_faqs: aiFaqs.value,
  lumerai_payment_policy: lumeraiPaymentPolicy.value,
});

const handleRealtimeVoiceEvent = event => {
  if (!voiceCallActive.value) return;
  if (event.type === 'response.function_call_arguments.done') {
    if (!event.response_id || !event.call_id || !event.name) return;
    pendingRealtimeToolCalls[event.response_id] ||= [];
    pendingRealtimeToolCalls[event.response_id].push(event);
  } else if (
    event.type === 'conversation.item.input_audio_transcription.completed'
  ) {
    const transcript = event.transcript?.trim();
    if (transcript)
      playgroundMessages.value.push({ role: 'user', content: transcript });
  } else if (
    event.type === 'response.output_audio_transcript.done' ||
    event.type === 'response.audio_transcript.done'
  ) {
    const transcript = cleanRealtimeTranscript(event.transcript || '');
    const last = playgroundMessages.value.at(-1);
    if (transcript && !(last?.role === 'ai' && last.content === transcript)) {
      playgroundMessages.value.push({ role: 'ai', content: transcript });
    }
  } else if (event.type === 'input_audio_buffer.speech_started') {
    voiceCallStatus.value = 'processing';
  } else if (event.type === 'response.created') {
    voiceCallStatus.value = 'speaking';
  } else if (event.type === 'response.done') {
    const response = event.response || {};
    const calls = pendingRealtimeToolCalls[response.id] || [];
    delete pendingRealtimeToolCalls[response.id];
    if (response.status === 'completed' && calls.length) {
      voiceCallStatus.value = 'processing';
      executeRealtimeToolCalls(calls);
    } else {
      voiceCallStatus.value = 'listening';
    }
  } else if (event.type === 'error') {
    useAlert(t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_CONNECTION_ERROR'));
    stopVoicePlayground();
  }
};

const waitForIceGathering = peer =>
  new Promise((resolve, reject) => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timeout = window.setTimeout(
      () => reject(new Error('ICE gathering timeout')),
      10000
    );
    peer.addEventListener('icegatheringstatechange', () => {
      if (peer.iceGatheringState === 'complete') {
        window.clearTimeout(timeout);
        resolve();
      }
    });
  });

const startVoicePlayground = async () => {
  if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
    useAlert(t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_UNSUPPORTED'));
    return;
  }

  voiceStartGeneration += 1;
  const generation = voiceStartGeneration;
  const greeting = t('LUMERAI_SETTINGS.PLAYGROUND.GREETING_WITH_NAME', {
    name: aiBotName.value || 'LumerAI',
  });
  const hadOnlyGreeting =
    playgroundMessages.value.length === 1 &&
    playgroundMessages.value[0].role === 'ai' &&
    [greeting, t('LUMERAI_SETTINGS.PLAYGROUND.GREETING')].includes(
      playgroundMessages.value[0].content
    );
  voiceCallActive.value = true;
  voiceCallStatus.value = 'processing';
  pendingRealtimeToolCalls = {};
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    if (generation !== voiceStartGeneration) {
      mediaStream.getTracks().forEach(track => track.stop());
      return;
    }
    voiceMediaStream = mediaStream;
    voicePeerConnection = new RTCPeerConnection();
    voiceMediaStream
      .getTracks()
      .forEach(track => voicePeerConnection.addTrack(track, voiceMediaStream));
    voiceRemoteAudio = new Audio();
    voiceRemoteAudio.autoplay = true;
    voicePeerConnection.ontrack = event => {
      if (!voiceCallActive.value) return;
      voiceRemoteAudio.srcObject =
        event.streams[0] || new MediaStream([event.track]);
      voiceRemoteAudio.play().catch(() => {
        useAlert(t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_AUDIO_BLOCKED'));
      });
    };
    voiceDataChannel = voicePeerConnection.createDataChannel('oai-events');
    voiceDataChannel.onmessage = message => {
      try {
        handleRealtimeVoiceEvent(JSON.parse(message.data));
      } catch (error) {
        // Ignore non-JSON or malformed server events.
      }
    };
    voiceDataChannel.onopen = () => {
      if (!voiceCallActive.value || voiceDataChannel?.readyState !== 'open') {
        return;
      }
      voiceCallStatus.value = 'listening';
      if (hadOnlyGreeting) playgroundMessages.value = [];
      const history = playgroundMessages.value.slice(-8);
      history.forEach(message => {
        if (!message.content) return;
        const role = message.role === 'user' ? 'user' : 'assistant';
        voiceDataChannel.send(
          JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role,
              content: [
                {
                  type: role === 'user' ? 'input_text' : 'text',
                  text: message.content,
                },
              ],
            },
          })
        );
      });
      voiceDataChannel.send(
        JSON.stringify({
          type: 'response.create',
          response: {
            output_modalities: ['audio'],
            instructions: `Cumprimente a pessoa brevemente como ${aiBotName.value || 'LumerAI'} e pergunte como pode ajudar. Fale no idioma da pessoa.`,
          },
        })
      );
    };

    const offer = await voicePeerConnection.createOffer();
    await voicePeerConnection.setLocalDescription(offer);
    await waitForIceGathering(voicePeerConnection);
    const response = await window.axios.post(
      `/api/v1/accounts/${currentAccount.value.id}/lumerai_playground/voice_session`,
      {
        sdp_offer: voicePeerConnection.localDescription.sdp,
        preview_attributes: playgroundPreviewAttributes(),
      }
    );
    if (generation !== voiceStartGeneration) {
      await window.axios.post(
        `/api/v1/accounts/${currentAccount.value.id}/lumerai_playground/close_voice_session`,
        { session_token: response.data.session_token }
      );
      return;
    }
    voiceSessionToken = response.data.session_token;
    await voicePeerConnection.setRemoteDescription({
      type: 'answer',
      sdp: response.data.sdp_answer,
    });
  } catch (error) {
    if (generation !== voiceStartGeneration) return;
    const denied = ['NotAllowedError', 'PermissionDeniedError'].includes(
      error.name
    );
    await stopVoicePlayground();
    if (hadOnlyGreeting && !playgroundMessages.value.length) {
      playgroundMessages.value.push({
        role: 'ai',
        content: greeting,
      });
    }
    useAlert(
      denied
        ? t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_MIC_ERROR')
        : t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_CONNECTION_ERROR')
    );
  }
};

stopVoicePlayground = async () => {
  voiceStartGeneration += 1;
  voiceCallActive.value = false;
  voiceCallStatus.value = 'listening';
  pendingRealtimeToolCalls = {};
  voiceDataChannel?.close();
  voiceDataChannel = null;
  voicePeerConnection?.close();
  voicePeerConnection = null;
  voiceMediaStream?.getTracks().forEach(track => track.stop());
  voiceMediaStream = null;
  if (voiceRemoteAudio) {
    voiceRemoteAudio.pause();
    voiceRemoteAudio.srcObject = null;
    voiceRemoteAudio = null;
  }
  const sessionToken = voiceSessionToken;
  voiceSessionToken = null;
  if (sessionToken) {
    try {
      await window.axios.post(
        `/api/v1/accounts/${currentAccount.value.id}/lumerai_playground/close_voice_session`,
        { session_token: sessionToken }
      );
    } catch (error) {
      // Closing local media is still guaranteed if server-side cleanup fails.
    }
  }
};

const toggleVoicePlayground = () => {
  if (voiceCallActive.value) stopVoicePlayground();
  else startVoicePlayground();
};

const updatePlaygroundGreeting = () => {
  if (
    playgroundMessages.value.length === 1 &&
    playgroundMessages.value[0].role === 'ai'
  ) {
    playgroundMessages.value[0].content = t(
      'LUMERAI_SETTINGS.PLAYGROUND.GREETING_WITH_NAME',
      { name: aiBotName.value || 'LumerAI' }
    );
  }
};

watch(locale, () => {
  updatePlaygroundGreeting();
});

onUnmounted(() => {
  stopVoicePlayground();
});

const handleKnowledgeUpdated = event => {
  const fields = event.detail || {};
  const refs = {
    ai_knowledge_base: aiKnowledgeBase,
    ai_prompt_persona: aiPromptPersona,
    ai_company_info: aiCompanyInfo,
    ai_company_name: aiCompanyName,
    ai_company_phone: aiCompanyPhone,
    ai_company_hours: aiCompanyHours,
    ai_company_email: aiCompanyEmail,
    ai_bot_name: aiBotName,
    ai_bot_gender: aiBotGender,
    ai_response_tone: aiResponseTone,
    ai_faqs: aiFaqs,
  };
  Object.entries(refs).forEach(([key, target]) => {
    if (fields[key] !== undefined) target.value = fields[key];
  });
};

onMounted(() => {
  window.addEventListener('lumerai-knowledge-updated', handleKnowledgeUpdated);
});

watch(
  () => currentAccount.value?.custom_attributes,
  newAttrs => {
    if (newAttrs) {
      aiPromptPersona.value = newAttrs.ai_prompt_persona || '';
      shopifyPolicy.value = {
        ...defaultShopifyPolicy(),
        ...(newAttrs.shopify_action_policy || {}),
      };
      shopifyActionInstructions.value = {
        ...defaultShopifyInstructions(),
        ...(newAttrs.shopify_action_instructions || {}),
      };
      shopifyCatalogResultLimit.value = Math.min(
        10,
        Math.max(1, Number(newAttrs.shopify_catalog_result_limit) || 10)
      );
      shopifyCatalogImagesOnly.value =
        newAttrs.shopify_catalog_images_only === true ||
        newAttrs.shopify_catalog_images_only === 'true';
      shopifyMetaCatalogEnabled.value =
        newAttrs.shopify_meta_catalog_enabled === true ||
        newAttrs.shopify_meta_catalog_enabled === 'true';
      shopifyMetaCatalogId.value = newAttrs.shopify_meta_catalog_id || '';
      shopifyMetaCatalogToken.value = '';
      shopifyMetaCatalogTokenConfigured.value =
        newAttrs.shopify_meta_catalog_token_configured === true;
      shopifyStoreName.value = newAttrs.shopify_store_name || '';
      aiKnowledgeBase.value = newAttrs.ai_knowledge_base || '';
      aiFaqs.value = Array.isArray(newAttrs.ai_faqs) ? newAttrs.ai_faqs : [];
      aiAutopilotInboxes.value = newAttrs.ai_autopilot_inboxes || [];
      lumeraiVoiceCallInboxes.value = (
        newAttrs.lumerai_voice_call_inboxes || []
      ).map(Number);
      lumeraiVoiceCallCapabilities.value = Array(
        newAttrs.lumerai_voice_call_capabilities || []
      ).map(entry => ({
        inbox_id: Number(entry.inbox_id),
        enabled_capabilities: Array.isArray(entry.enabled_capabilities)
          ? entry.enabled_capabilities.map(String)
          : [...defaultVoiceCallCapabilities],
      }));
      lumeraiVoiceTranscriptsEnabled.value =
        newAttrs.lumerai_voice_call_transcripts_enabled === true ||
        newAttrs.lumerai_voice_call_transcripts_enabled === 'true';
      lumeraiRealtimeVoice.value = realtimeVoiceOptions.includes(
        newAttrs.lumerai_realtime_voice
      )
        ? newAttrs.lumerai_realtime_voice
        : 'marin';
      aiAutopilotDebounceSeconds.value = normalizeAutopilotDebounceSeconds(
        newAttrs.lumerai_autopilot_debounce_seconds
      );
      lumeraiPaymentPolicy.value = {
        ...defaultPaymentPolicy(),
        ...(newAttrs.lumerai_payment_policy || {}),
        boleto: {
          ...defaultPaymentPolicy().boleto,
          ...(newAttrs.lumerai_payment_policy?.boleto || {}),
        },
        pix: {
          ...defaultPaymentPolicy().pix,
          ...(newAttrs.lumerai_payment_policy?.pix || {}),
        },
        credit_card_installments:
          newAttrs.lumerai_payment_policy?.credit_card_installments ||
          defaultPaymentPolicy().credit_card_installments,
      };
      lumeraiAutoResolveEnabled.value =
        newAttrs.lumerai_auto_resolve_enabled === true ||
        newAttrs.lumerai_auto_resolve_enabled === 'true' ||
        newAttrs.lumerai_auto_resolve_enabled === '1';
      lumeraiSalesPipelineAutomationEnabled.value =
        newAttrs.lumerai_sales_pipeline_automation_enabled === true ||
        newAttrs.lumerai_sales_pipeline_automation_enabled === 'true' ||
        newAttrs.lumerai_sales_pipeline_automation_enabled === '1';
      aiBotGender.value = newAttrs.ai_bot_gender || 'neutral';
      aiResponseTone.value = newAttrs.ai_response_tone || 'professional';
      aiStickersEnabled.value =
        newAttrs.ai_stickers_enabled === true ||
        newAttrs.ai_stickers_enabled === 'true' ||
        newAttrs.ai_stickers_enabled === '1';

      aiBotName.value = newAttrs.ai_bot_name || '';
      aiCompanyName.value = newAttrs.ai_company_name || '';
      aiCompanyPhone.value = formatCompanyPhone(
        newAttrs.ai_company_phone || ''
      );
      aiCompanyHours.value = newAttrs.ai_company_hours || '';
      aiCompanyEmail.value = newAttrs.ai_company_email || '';
      aiCompanyInfo.value = newAttrs.ai_company_info || '';
      lumeraiTransferRules.value = Array.isArray(
        newAttrs.lumerai_transfer_rules
      )
        ? newAttrs.lumerai_transfer_rules
        : [];

      ixcUrl.value = newAttrs.ixc_url || '';
      ixcToken.value = newAttrs.ixc_token || '';

      mkauthUrl.value = newAttrs.mkauth_url || '';
      mkauthClientId.value = newAttrs.mkauth_client_id || '';
      mkauthClientSecret.value = newAttrs.mkauth_client_secret || '';

      hubsoftUrl.value = newAttrs.hubsoft_url || '';
      hubsoftToken.value = newAttrs.hubsoft_token || '';
      if (!aiWebhooks.value.length && newAttrs.ai_webhooks?.length) {
        aiWebhooks.value = newAttrs.ai_webhooks;
      }

      updatePlaygroundGreeting();
    }
  },
  { immediate: true, deep: true }
);

const knowledgeDocumentsEndpoint = computed(
  () => `/api/v1/accounts/${currentAccount.value.id}/lumerai/documents`
);

const lumeraiStickersEndpoint = computed(() => {
  if (!currentAccount.value?.id) return '';
  return `/api/v1/accounts/${currentAccount.value.id}/lumerai_stickers`;
});

const nativeIntegrationsEndpoint = computed(() => {
  if (!currentAccount.value?.id) return '';
  return `/api/v1/accounts/${currentAccount.value.id}/native_integrations`;
});

const transferTargetsEndpoint = computed(() => {
  if (!currentAccount.value?.id) return '';
  return `/api/v1/accounts/${currentAccount.value.id}`;
});

const salesPipelinesEndpoint = computed(() => {
  if (!currentAccount.value?.id) return '';
  return `/api/v1/accounts/${currentAccount.value.id}/sales_pipelines`;
});

const namedCalendarsEndpoint = computed(() => {
  if (!currentAccount.value?.id) return '';
  return `/api/v1/accounts/${currentAccount.value.id}/calendar/routing_calendars`;
});

const defaultCalendarInstruction = calendar =>
  `Quando o cliente solicitar um agendamento relacionado a ${calendar.name}, use este calendário. Confirme o serviço, consulte a disponibilidade, ofereça horários livres e registre o compromisso somente após a confirmação do cliente. Respeite conflitos, bloqueios e as regras de disponibilidade do calendário.`;

const fetchLumeraiCalendars = async () => {
  if (!namedCalendarsEndpoint.value) return;

  isLoadingLumeraiCalendars.value = true;
  try {
    const { data } = await window.axios.get(namedCalendarsEndpoint.value);
    lumeraiCalendars.value = (data.payload || []).map(calendar => ({
      ...calendar,
      settings: calendar.settings || {},
      lumerai_instruction: calendar.settings?.lumerai_instruction || '',
    }));
  } catch (error) {
    lumeraiCalendars.value = [];
  } finally {
    isLoadingLumeraiCalendars.value = false;
  }
};

const saveLumeraiCalendarInstruction = async calendar => {
  if (!namedCalendarsEndpoint.value || isSavingLumeraiCalendar.value) return;

  isSavingLumeraiCalendar.value = calendar.id;
  try {
    await window.axios.put(`${namedCalendarsEndpoint.value}/${calendar.id}`, {
      calendar_routing_calendar: {
        settings: {
          ...calendar.settings,
          lumerai_instruction: calendar.lumerai_instruction?.trim() || '',
        },
      },
    });
    useAlert(`Orientação do calendário ${calendar.name} salva.`);
    await fetchLumeraiCalendars();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        `Não foi possível salvar a orientação de ${calendar.name}.`
    );
  } finally {
    isSavingLumeraiCalendar.value = null;
  }
};

const defaultPipelineInstruction = stage => {
  const defaults = {
    Entrada:
      'Identifique se o cliente demonstrou interesse comercial e se há contexto suficiente para iniciar a qualificação.',
    'Revisão necessária':
      'Use esta etapa quando faltarem dados mínimos, houver ambiguidade ou a conversa não tiver intenção comercial clara.',
    Qualificação:
      'Considere qualificada somente uma oportunidade com interesse claro e necessidade, produto ou serviço identificável.',
    Proposta:
      'Esta etapa é utilizada quando uma proposta comercial foi preparada ou enviada por um atendente.',
    Negociação:
      'Esta etapa é utilizada quando existem condições, valores, prazos ou objeções sendo negociados com o cliente.',
    Ganho:
      'Use somente após confirmação humana de fechamento. A LumerAI nunca move oportunidades para esta etapa.',
    Perdido:
      'Use somente após confirmação humana de perda e registro do motivo. A LumerAI nunca move oportunidades para esta etapa.',
  };

  return (
    defaults[stage.name] ||
    'Descreva como a LumerAI deve interpretar esta etapa.'
  );
};

const fetchLumeraiPipeline = async () => {
  if (!salesPipelinesEndpoint.value) return;

  isLoadingLumeraiPipeline.value = true;
  lumeraiPipelineError.value = '';
  try {
    const { data } = await window.axios.get(salesPipelinesEndpoint.value);
    const pipeline =
      (data.payload || []).find(item => item.is_default) || data.payload?.[0];
    lumeraiPipeline.value = pipeline || null;
    lumeraiPipelineStages.value = pipeline
      ? pipeline.stages.map(stage => ({ ...stage }))
      : [];
  } catch (error) {
    lumeraiPipeline.value = null;
    lumeraiPipelineStages.value = [];
    lumeraiPipelineError.value =
      error.response?.data?.error ||
      'Não foi possível carregar o Pipeline de Vendas desta conta.';
  } finally {
    isLoadingLumeraiPipeline.value = false;
  }
};

const saveLumeraiPipelineGuidance = async () => {
  if (!lumeraiPipeline.value || isSavingLumeraiPipeline.value) return;

  isSavingLumeraiPipeline.value = true;
  try {
    await Promise.all(
      lumeraiPipelineStages.value.map(stage =>
        window.axios.patch(
          `${salesPipelinesEndpoint.value}/${lumeraiPipeline.value.id}/stages/${stage.id}`,
          {
            sales_pipeline_stage: {
              lumerai_instruction: stage.lumerai_instruction?.trim() || '',
            },
          }
        )
      )
    );
    useAlert('Orientações da LumerAI para o Pipeline salvas.');
    await fetchLumeraiPipeline();
  } catch (error) {
    useAlert('Não foi possível salvar as orientações do Pipeline.');
  } finally {
    isSavingLumeraiPipeline.value = false;
  }
};

const fetchTransferTargets = async () => {
  if (!transferTargetsEndpoint.value) return;

  try {
    const [teamsResponse, usersResponse] = await Promise.all([
      window.axios.get(`${transferTargetsEndpoint.value}/teams`),
      window.axios.get(`${transferTargetsEndpoint.value}/agents`),
    ]);
    transferTeams.value = teamsResponse.data;
    transferUsers.value = usersResponse.data;
  } catch (error) {
    useAlert(
      'Não foi possível carregar equipes e usuários para transferência.'
    );
  }
};

const addTransferRule = () => {
  lumeraiTransferRules.value.push({
    id: crypto.randomUUID(),
    criterion: '',
    target_type: 'team',
    target_id: '',
  });
};

const removeTransferRule = index => {
  lumeraiTransferRules.value.splice(index, 1);
};

const fetchAbmIntegration = async () => {
  if (!nativeIntegrationsEndpoint.value) return;

  try {
    const { data } = await window.axios.get(nativeIntegrationsEndpoint.value);
    const sgp = data.find(item => item.key === 'sgp');
    sgpIntegration.value = sgp || null;
    sgpUsage.value = { ...defaultSgpUsage(), ...(sgp?.settings || {}) };
    sgpActionInstructions.value = parseSgpActionInstructions(
      sgp?.settings?.sgp_action_instructions
    );
    const integration = data.find(item => item.key === 'abm');
    abmIntegration.value = integration || null;
    abmCatalogEnabled.value =
      integration?.settings?.abm_catalog_enabled === true ||
      integration?.settings?.abm_catalog_enabled === 'true' ||
      integration?.settings?.abm_catalog_enabled === '1';
    abmCatalogResultLimit.value = Math.min(
      10,
      Math.max(1, Number(integration?.settings?.abm_catalog_result_limit) || 3)
    );
    abmCatalogImagesOnly.value =
      integration?.settings?.abm_catalog_images_only === true ||
      integration?.settings?.abm_catalog_images_only === 'true';
    abmCatalogUsageInstruction.value =
      integration?.settings?.abm_catalog_usage_instruction || '';
    abmClientLookupUsageInstruction.value =
      integration?.settings?.abm_client_lookup_usage_instruction || '';
    shopifyIntegration.value =
      data.find(item => item.key === 'shopify') || null;
    siprovIntegration.value = data.find(item => item.key === 'siprov') || null;
    siprovUsage.value = {
      ...defaultSiprovUsage(),
      ...(siprovIntegration.value?.settings || {}),
    };
  } catch (error) {
    abmIntegration.value = null;
    sgpIntegration.value = null;
    shopifyIntegration.value = null;
    siprovIntegration.value = null;
  }
};

const fetchLumeraiStickers = async () => {
  if (!lumeraiStickersEndpoint.value) return;

  isLoadingStickers.value = true;
  try {
    const response = await window.axios.get(lumeraiStickersEndpoint.value);
    lumeraiStickers.value = response.data;
  } catch (error) {
    useAlert('Não foi possível carregar as figurinhas da LumerAI.');
  } finally {
    isLoadingStickers.value = false;
  }
};

const onStickerFileChange = event => {
  const [file] = event.target.files || [];
  if (!file) return;

  const isWebp =
    file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp');
  if (!isWebp || file.size > 100 * 1024) {
    useAlert('Envie uma figurinha WebP de até 100 KB.');
    event.target.value = '';
    stickerFile.value = null;
    return;
  }

  stickerFile.value = file;
};

const createLumeraiSticker = async () => {
  if (
    !stickerName.value.trim() ||
    !stickerInstruction.value.trim() ||
    !stickerFile.value
  ) {
    useAlert(
      'Informe o nome, quando a IA pode usar a figurinha e o arquivo WebP.'
    );
    return;
  }

  isSavingSticker.value = true;
  try {
    const payload = new FormData();
    payload.append('sticker[name]', stickerName.value.trim());
    payload.append('sticker[instruction]', stickerInstruction.value.trim());
    payload.append('sticker[file]', stickerFile.value);
    await window.axios.post(lumeraiStickersEndpoint.value, payload);
    stickerName.value = '';
    stickerInstruction.value = '';
    stickerFile.value = null;
    if (stickerFileInput.value) stickerFileInput.value.value = '';
    await fetchLumeraiStickers();
    useAlert('Figurinha adicionada e autorizada para a LumerAI.');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.join(', ') ||
        'Não foi possível adicionar a figurinha.'
    );
  } finally {
    isSavingSticker.value = false;
  }
};

const toggleLumeraiSticker = async sticker => {
  try {
    const response = await window.axios.patch(
      `${lumeraiStickersEndpoint.value}/${sticker.id}`,
      { sticker: { active: !sticker.active } }
    );
    const index = lumeraiStickers.value.findIndex(
      item => item.id === sticker.id
    );
    if (index >= 0) lumeraiStickers.value.splice(index, 1, response.data);
  } catch (error) {
    useAlert('Não foi possível atualizar a autorização da figurinha.');
  }
};

const removeLumeraiSticker = async sticker => {
  confirmTitle.value = 'Remover figurinha';
  confirmDescription.value = `Deseja remover “${sticker.name}” do catálogo autorizado da LumerAI?`;
  const confirmed = await confirmDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    await window.axios.delete(`${lumeraiStickersEndpoint.value}/${sticker.id}`);
    lumeraiStickers.value = lumeraiStickers.value.filter(
      item => item.id !== sticker.id
    );
    useAlert('Figurinha removida.');
  } catch (error) {
    useAlert('Não foi possível remover a figurinha.');
  }
};

watch(
  () => currentAccount.value?.id,
  accountId => {
    if (accountId) {
      fetchLumeraiStickers();
      fetchAbmIntegration();
      fetchTransferTargets();
      fetchLumeraiPipeline();
      fetchLumeraiCalendars();
    }
  },
  { immediate: true }
);

const fetchKnowledgeDocuments = async () => {
  if (!currentAccount.value?.id) return;

  isLoadingKnowledgeDocuments.value = true;
  try {
    const response = await window.axios.get(knowledgeDocumentsEndpoint.value, {
      params: { autopilot: true, sort: 'recently_updated' },
    });
    knowledgeDocuments.value = response.data.payload || [];
    knowledgeDocumentsMeta.value = response.data.meta || {};
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar as fontes de conhecimento.'
    );
  } finally {
    isLoadingKnowledgeDocuments.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchKnowledgeDocuments();
});

const createKnowledgeUrl = async () => {
  const externalLink = knowledgeSourceUrl.value.trim();
  if (!externalLink) {
    useAlert('Informe uma URL válida para adicionar à base.');
    return;
  }

  isCreatingKnowledgeDocument.value = true;
  try {
    await window.axios.post(knowledgeDocumentsEndpoint.value, {
      document: {
        external_link: externalLink,
        name: knowledgeSourceName.value.trim() || externalLink,
        autopilot: true,
      },
    });
    knowledgeSourceUrl.value = '';
    knowledgeSourceName.value = '';
    useAlert('URL adicionada. A LumerAI iniciou o processamento.');
    await fetchKnowledgeDocuments();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível adicionar esta URL.'
    );
  } finally {
    isCreatingKnowledgeDocument.value = false;
  }
};

const selectKnowledgePdf = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (file.type !== 'application/pdf') {
    useAlert('Selecione um arquivo PDF.');
    event.target.value = '';
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    useAlert('O PDF deve ter no máximo 10 MB.');
    event.target.value = '';
    return;
  }
  knowledgePdfFile.value = file;
};

const uploadKnowledgePdf = async () => {
  if (!knowledgePdfFile.value) {
    useAlert('Selecione um PDF antes de enviar.');
    return;
  }

  isCreatingKnowledgeDocument.value = true;
  try {
    const formData = new FormData();
    formData.append('document[pdf_file]', knowledgePdfFile.value);
    formData.append(
      'document[name]',
      knowledgePdfFile.value.name.replace(/\.pdf$/i, '')
    );
    formData.append('document[autopilot]', 'true');
    await window.axios.post(knowledgeDocumentsEndpoint.value, formData);
    knowledgePdfFile.value = null;
    if (knowledgeFileInput.value) knowledgeFileInput.value.value = '';
    useAlert('PDF enviado. A LumerAI iniciou o processamento.');
    await fetchKnowledgeDocuments();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível enviar este PDF.'
    );
  } finally {
    isCreatingKnowledgeDocument.value = false;
  }
};

const removeKnowledgeDocument = async document => {
  confirmTitle.value = 'Remover fonte de conhecimento';
  confirmDescription.value = `Deseja remover “${document.name}” e as respostas geradas a partir desta fonte?`;
  const confirmed = await confirmDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    await window.axios.delete(
      `${knowledgeDocumentsEndpoint.value}/${document.id}`
    );
    useAlert('Fonte removida da base de conhecimento.');
    await fetchKnowledgeDocuments();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível remover a fonte.'
    );
  }
};

const syncKnowledgeDocument = async document => {
  try {
    await window.axios.post(
      `${knowledgeDocumentsEndpoint.value}/${document.id}/sync`
    );
    useAlert('Sincronização iniciada.');
    await fetchKnowledgeDocuments();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível sincronizar esta fonte.'
    );
  }
};

const knowledgeDocumentStatus = document => {
  if (document.sync_status === 'failed') return 'Falha na sincronização';
  if (document.sync_in_progress || document.status === 'in_progress')
    return 'Processando';
  if (document.sync_status === 'synced' || document.status === 'available')
    return 'Disponível';
  return 'Aguardando';
};

const saveSettings = async () => {
  isUpdating.value = true;
  try {
    const updatedAttributes = {
      ...currentAccount.value.custom_attributes,
      ai_prompt_persona: aiPromptPersona.value,
      ai_knowledge_base: aiKnowledgeBase.value,
      ai_faqs: aiFaqs.value,
      ai_autopilot_inboxes: aiAutopilotInboxes.value,
      lumerai_autopilot_debounce_seconds: normalizeAutopilotDebounceSeconds(
        aiAutopilotDebounceSeconds.value
      ),
      lumerai_payment_policy: lumeraiPaymentPolicy.value,
      lumerai_auto_resolve_enabled: lumeraiAutoResolveEnabled.value
        ? 'true'
        : 'false',
      lumerai_sales_pipeline_automation_enabled:
        lumeraiSalesPipelineAutomationEnabled.value ? 'true' : 'false',
      lumerai_realtime_voice: lumeraiRealtimeVoice.value,
      ai_bot_gender: aiBotGender.value,
      ai_response_tone: aiResponseTone.value,
      ai_stickers_enabled: aiStickersEnabled.value ? 'true' : 'false',
      ai_bot_name: aiBotName.value,
      ai_company_name: aiCompanyName.value,
      ai_company_phone: aiCompanyPhone.value,
      ai_company_hours: aiCompanyHours.value,
      ai_company_email: aiCompanyEmail.value,
      ai_company_info: aiCompanyInfo.value,
      ixc_url: ixcUrl.value,
      ixc_token: ixcToken.value,
      mkauth_url: mkauthUrl.value,
      mkauth_client_id: mkauthClientId.value,
      mkauth_client_secret: mkauthClientSecret.value,
      hubsoft_url: hubsoftUrl.value,
      hubsoft_token: hubsoftToken.value,
      ai_webhooks: aiWebhooks.value,
      lumerai_transfer_rules: lumeraiTransferRules.value,
    };

    await updateAccount({ custom_attributes: updatedAttributes });
    useAlert(
      'Configurações da Inteligência Artificial atualizadas com sucesso!'
    );
  } catch (error) {
    useAlert('Erro ao atualizar configurações da IA.');
  } finally {
    isUpdating.value = false;
  }
};

const saveLumeraiPipelineAutomation = async () => {
  // Read the v-model after the switch has toggled it. Depending on the
  // rendered Switch version, the change payload may be a DOM event rather
  // than the boolean value.
  const enabled = lumeraiSalesPipelineAutomationEnabled.value === true;
  const previousValue = enabled ? 'false' : 'true';
  isSavingLumeraiPipelineAutomation.value = true;

  try {
    await updateAccount(
      {
        custom_attributes: {
          lumerai_sales_pipeline_automation_enabled: enabled ? 'true' : 'false',
        },
      },
      { silent: true }
    );
    // Account updates may return a partial/stale account representation. Keep
    // the switch aligned with the value that was just persisted.
    lumeraiSalesPipelineAutomationEnabled.value = enabled;
    useAlert(
      enabled
        ? 'Automação do Pipeline pela LumerAI ativada.'
        : 'Automação do Pipeline pela LumerAI desativada.'
    );
  } catch (error) {
    lumeraiSalesPipelineAutomationEnabled.value = previousValue === 'true';
    useAlert(
      'Não foi possível salvar a configuração de automação do Pipeline.'
    );
  } finally {
    isSavingLumeraiPipelineAutomation.value = false;
  }
};

const saveStickerPermissions = async nextValue => {
  const enabled = Boolean(nextValue);
  const savedValue =
    currentAccount.value?.custom_attributes?.ai_stickers_enabled === true ||
    currentAccount.value?.custom_attributes?.ai_stickers_enabled === 'true' ||
    currentAccount.value?.custom_attributes?.ai_stickers_enabled === '1';

  isSavingStickerPermissions.value = true;
  try {
    // This authorization controls what the assistant can send. Persist it as
    // soon as it changes instead of leaving a misleading, unsaved UI state.
    await updateAccount(
      {
        custom_attributes: {
          ai_stickers_enabled: enabled ? 'true' : 'false',
        },
      },
      { silent: true }
    );
    useAlert(
      enabled
        ? 'Envio de figurinhas habilitado.'
        : 'Envio de figurinhas desabilitado.'
    );
  } catch (error) {
    aiStickersEnabled.value = savedValue;
    useAlert('Não foi possível salvar a permissão de figurinhas.');
  } finally {
    isSavingStickerPermissions.value = false;
  }
};

const saveAbmUsageInstructions = async () => {
  if (isSavingAbmUsage.value || !nativeIntegrationsEndpoint.value) return;

  abmCatalogResultLimit.value = Math.min(
    10,
    Math.max(1, Number(abmCatalogResultLimit.value) || 3)
  );
  isSavingAbmUsage.value = true;
  try {
    const { data } = await window.axios.patch(
      `${nativeIntegrationsEndpoint.value}/abm`,
      {
        settings: {
          abm_catalog_enabled: abmCatalogEnabled.value,
          abm_catalog_result_limit: abmCatalogResultLimit.value,
          abm_catalog_images_only: abmCatalogImagesOnly.value,
          abm_catalog_usage_instruction:
            abmCatalogUsageInstruction.value.trim(),
          abm_client_lookup_usage_instruction:
            abmClientLookupUsageInstruction.value.trim(),
        },
      }
    );
    abmIntegration.value = data;
    useAlert('Regras de uso da integração ABM atualizadas.');
  } catch (error) {
    useAlert('Não foi possível salvar as regras de uso da integração ABM.');
  } finally {
    isSavingAbmUsage.value = false;
  }
};

const saveShopifyUsage = async () => {
  if (isSavingShopifyUsage.value) return;

  shopifyCatalogResultLimit.value = Math.min(
    10,
    Math.max(1, Number(shopifyCatalogResultLimit.value) || 10)
  );
  isSavingShopifyUsage.value = true;
  try {
    if (shopifyMetaCatalogToken.value.trim()) {
      await window.axios.patch(
        `/api/v1/accounts/${currentAccount.value.id}/integrations/shopify/catalog_token`,
        { token: shopifyMetaCatalogToken.value.trim() }
      );
      shopifyMetaCatalogToken.value = '';
      shopifyMetaCatalogTokenConfigured.value = true;
    }
    await updateAccount(
      {
        custom_attributes: {
          ...currentAccount.value.custom_attributes,
          shopify_action_policy: shopifyPolicy.value,
          shopify_action_instructions: shopifyActionInstructions.value,
          shopify_catalog_result_limit: shopifyCatalogResultLimit.value,
          shopify_catalog_images_only: shopifyCatalogImagesOnly.value,
          shopify_meta_catalog_enabled: shopifyMetaCatalogEnabled.value,
          shopify_meta_catalog_id: shopifyMetaCatalogId.value,
          shopify_store_name: shopifyStoreName.value,
        },
      },
      { silent: true }
    );
    useAlert('Permissões e orientações da Shopify atualizadas.');
  } catch (error) {
    useAlert('Não foi possível salvar a orientação da Shopify.');
  } finally {
    isSavingShopifyUsage.value = false;
  }
};

const saveSgpUsage = async () => {
  if (isSavingSgpUsage.value || !nativeIntegrationsEndpoint.value) return;

  isSavingSgpUsage.value = true;
  try {
    const { data } = await window.axios.patch(
      `${nativeIntegrationsEndpoint.value}/sgp`,
      {
        settings: {
          ...sgpUsage.value,
          sgp_usage_instruction:
            sgpUsage.value.sgp_usage_instruction?.trim() || '',
          sgp_action_instructions: JSON.stringify(sgpActionInstructions.value),
        },
      }
    );
    sgpIntegration.value = data;
    sgpUsage.value = { ...defaultSgpUsage(), ...(data.settings || {}) };
    sgpActionInstructions.value = parseSgpActionInstructions(
      data.settings?.sgp_action_instructions
    );
    useAlert('Permissões e orientações do SGP atualizadas.');
  } catch (error) {
    useAlert('Não foi possível salvar as permissões do SGP.');
  } finally {
    isSavingSgpUsage.value = false;
  }
};

const saveSiprovUsage = async () => {
  if (isSavingSiprovUsage.value || !nativeIntegrationsEndpoint.value) return;

  isSavingSiprovUsage.value = true;
  try {
    const { data } = await window.axios.patch(
      `${nativeIntegrationsEndpoint.value}/siprov`,
      {
        settings: {
          ...siprovUsage.value,
          siprov_usage_instruction:
            siprovUsage.value.siprov_usage_instruction?.trim() || '',
        },
      }
    );
    siprovIntegration.value = data;
    siprovUsage.value = { ...defaultSiprovUsage(), ...(data.settings || {}) };
    useAlert('Permissões e orientações do Siprov atualizadas.');
  } catch (error) {
    useAlert('Não foi possível salvar as permissões do Siprov.');
  } finally {
    isSavingSiprovUsage.value = false;
  }
};

const toggleInbox = async inboxId => {
  const id = Number(inboxId);
  const isCurrentlyOn = aiAutopilotInboxes.value.map(Number).includes(id);

  if (isCurrentlyOn) {
    confirmTitle.value = 'Desativar Inteligência Artificial';
    confirmDescription.value =
      'Tem certeza de que deseja desativar o piloto automático desta caixa? O atendimento passará a ser feito por humanos ou por algum Workflow ativo.';
    const ok = await confirmDialog.value.showConfirmation();
    if (!ok) return;

    aiAutopilotInboxes.value = aiAutopilotInboxes.value
      .map(Number)
      .filter(i => i !== id);
    saveSettings();
  } else {
    try {
      const response = await window.axios.get(
        `/api/v1/accounts/${currentAccount.value.id}/lumerai_flows`
      );
      const activeOverrideFlow = response.data.find(
        f =>
          (f.inbox_id === id || f.inbox_id?.toString() === id.toString()) &&
          f.is_active &&
          !f.is_fallback
      );

      if (activeOverrideFlow) {
        confirmTitle.value = 'Conflito de Atendimento';
        confirmDescription.value = `Esta caixa já possui o Workflow "${activeOverrideFlow.name}" configurado como "Assumir 100%". Se você ativar a Inteligência Artificial, o sistema irá automaticamente desativar aquele fluxo para não haver choque de comandos. Tem certeza de que deseja ativar a IA?`;
        const ok = await confirmDialog.value.showConfirmation();
        if (!ok) return;

        // User confirmed. We deactivate the conflicting flow.
        await window.axios.patch(
          `/api/v1/accounts/${currentAccount.value.id}/lumerai_flows/${activeOverrideFlow.id}`,
          {
            is_active: false,
          }
        );
      }

      aiAutopilotInboxes.value.push(id);
      saveSettings();
    } catch (err) {
      // In case of error, just proceed with normal toggle
      aiAutopilotInboxes.value.push(id);
      saveSettings();
    }
  }
};

const whatsappVoiceInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === 'Channel::Whatsapp')
);

const toggleVoiceCallInbox = async inboxId => {
  const id = Number(inboxId);
  const previous = [...lumeraiVoiceCallInboxes.value];
  lumeraiVoiceCallInboxes.value = previous.includes(id)
    ? previous.filter(inboxIdValue => inboxIdValue !== id)
    : [...previous, id];

  try {
    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        lumerai_voice_call_inboxes: lumeraiVoiceCallInboxes.value,
      },
    });
    useAlert('Configuração de atendimento por voz atualizada.');
  } catch (error) {
    lumeraiVoiceCallInboxes.value = previous;
    useAlert('Não foi possível atualizar o atendimento por voz.');
  }
};

const getVoiceCallCapabilities = inboxId => {
  const entry = lumeraiVoiceCallCapabilities.value.find(
    item => item.inbox_id === Number(inboxId)
  );
  return entry?.enabled_capabilities || defaultVoiceCallCapabilities;
};

const toggleVoiceCallCapability = async (inboxId, capability) => {
  const id = Number(inboxId);
  if (savingVoiceCallCapabilities.value.includes(id)) return;

  const previous = lumeraiVoiceCallCapabilities.value.map(entry => ({
    ...entry,
    enabled_capabilities: [...entry.enabled_capabilities],
  }));
  const enabled = new Set(getVoiceCallCapabilities(id));
  if (enabled.has(capability)) enabled.delete(capability);
  else enabled.add(capability);

  const nextCapabilities = [...enabled].filter(key =>
    defaultVoiceCallCapabilities.includes(key)
  );
  const nextSettings = previous.filter(entry => entry.inbox_id !== id);
  // No override means inherit the current chat capabilities (the compatibility
  // default for existing accounts).
  if (
    nextCapabilities.length !== defaultVoiceCallCapabilities.length ||
    !defaultVoiceCallCapabilities.every(key => nextCapabilities.includes(key))
  ) {
    nextSettings.push({ inbox_id: id, enabled_capabilities: nextCapabilities });
  }
  lumeraiVoiceCallCapabilities.value = nextSettings;
  savingVoiceCallCapabilities.value = [
    ...savingVoiceCallCapabilities.value,
    id,
  ];

  try {
    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        lumerai_voice_call_capabilities: nextSettings,
      },
    });
    useAlert('Capacidades da LumerAI na ligação atualizadas.');
  } catch {
    lumeraiVoiceCallCapabilities.value = previous;
    useAlert('Não foi possível atualizar as capacidades da ligação.');
  } finally {
    savingVoiceCallCapabilities.value =
      savingVoiceCallCapabilities.value.filter(
        inboxIdValue => inboxIdValue !== id
      );
  }
};

const toggleVoiceTranscriptCapture = async () => {
  const previous = !lumeraiVoiceTranscriptsEnabled.value;
  try {
    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        lumerai_voice_call_transcripts_enabled:
          lumeraiVoiceTranscriptsEnabled.value,
      },
    });
    useAlert('Preferência de transcrição atualizada.');
  } catch (error) {
    lumeraiVoiceTranscriptsEnabled.value = previous;
    useAlert('Não foi possível atualizar a preferência de transcrição.');
  }
};

const handleTabChanged = tab => {
  activeTabIndex.value = tab.value;
};

sendPlaygroundMessage = async message => {
  const userText = (message ?? playgroundInput.value).trim();
  if (!userText) return null;

  playgroundMessages.value.push({ role: 'user', content: userText });
  playgroundInput.value = '';
  isSimulating.value = true;

  try {
    const response = await window.axios.post(
      '/api/v1/accounts/' + currentAccount.value.id + '/lumerai_playground',
      {
        message_content: userText,
        message_history: playgroundMessages.value.slice(0, -1).map(msg => ({
          role:
            msg.role === 'ai' || msg.role === 'assistant'
              ? 'assistant'
              : 'user',
          content: msg.content,
        })),
        preview_attributes: {
          ai_prompt_persona: aiPromptPersona.value,
          ai_knowledge_base: aiKnowledgeBase.value,
          ai_bot_name: aiBotName.value,
          ai_bot_gender: aiBotGender.value,
          ai_response_tone: aiResponseTone.value,
          ai_company_name: aiCompanyName.value,
          ai_company_phone: aiCompanyPhone.value,
          ai_company_email: aiCompanyEmail.value,
          ai_company_hours: aiCompanyHours.value,
          ai_company_info: aiCompanyInfo.value,
          ai_faqs: aiFaqs.value,
        },
        // O Playground não tem uma conversa real para extrair o contato. Na
        // Shopify, estes dados simulam o contexto recebido pelo canal para a
        // consulta de pedidos. Outras integrações não dependem deles.
        ...(shopifyIntegrationAvailable.value
          ? {
              contact_phone: playgroundContactPhone.value,
              contact_email: playgroundContactEmail.value,
            }
          : {}),
      }
    );

    let aiResponse = response.data.response;
    if (typeof aiResponse === 'object' && aiResponse !== null) {
      if (aiResponse.type === 'text') {
        aiResponse = aiResponse.content;
      } else if (aiResponse.type === 'tool_calls') {
        const functionName =
          aiResponse.message?.function_call?.name ||
          aiResponse.message?.tool_calls?.[0]?.function?.name ||
          aiResponse.message?.parts?.[0]?.functionCall?.name ||
          'desconhecida';
        aiResponse = `[A IA executou a ferramenta: ${functionName}]`;
      } else {
        aiResponse = JSON.stringify(aiResponse);
      }
    }

    playgroundMessages.value.push({
      role: 'ai',
      content: aiResponse || 'Desculpe, ocorreu um erro na resposta.',
      stickers: Array.isArray(response.data.stickers)
        ? response.data.stickers
        : [],
    });
    return aiResponse;
  } catch (error) {
    const errorMsg =
      error.response && error.response.data && error.response.data.error
        ? error.response.data.error
        : 'Erro de conexão com o servidor. Tente novamente mais tarde.';
    playgroundMessages.value.push({
      role: 'ai',
      content: errorMsg,
    });
    return errorMsg;
  } finally {
    isSimulating.value = false;
  }
};

const clearPlayground = () => {
  if (voiceCallActive.value) stopVoicePlayground();
  const botName = aiBotName.value || 'LumerAI';
  playgroundMessages.value = [
    {
      role: 'ai',
      content: t('LUMERAI_SETTINGS.PLAYGROUND.GREETING_WITH_NAME', {
        name: botName,
      }),
    },
  ];
};

const addWebhook = () => {
  aiWebhooks.value.push({
    id: Math.random().toString(36).substr(2, 9),
    name: '',
    description: '',
    url: '',
    method: 'POST',
    headers: [],
    parameters: [],
  });
};

const removeWebhook = index => {
  aiWebhooks.value.splice(index, 1);
};

const addParameter = webhookIndex => {
  aiWebhooks.value[webhookIndex].parameters.push({ name: '', description: '' });
};

const removeParameter = (webhookIndex, paramIndex) => {
  aiWebhooks.value[webhookIndex].parameters.splice(paramIndex, 1);
};

const addHeader = webhookIndex => {
  if (!aiWebhooks.value[webhookIndex].headers) {
    aiWebhooks.value[webhookIndex].headers = [];
  }
  aiWebhooks.value[webhookIndex].headers.push({ name: '', value: '' });
};

const removeHeader = (webhookIndex, headerIndex) => {
  aiWebhooks.value[webhookIndex].headers.splice(headerIndex, 1);
};

const addFaq = () => {
  aiFaqs.value.push({
    id: Math.random().toString(36).substr(2, 9),
    question: '',
    answer: '',
  });
};

const removeFaq = index => {
  aiFaqs.value.splice(index, 1);
};

const formatMessage = content => {
  if (!content) return '';
  let escaped = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Images
  escaped = escaped.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" class="max-w-full rounded-md my-2 shadow-sm border border-n-strong" />'
  );

  // Links
  escaped = escaped.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" class="text-n-brand underline hover:text-n-brand-dark">$1</a>'
  );

  // Bold
  escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic
  escaped = escaped.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  return escaped.replace(/\n/g, '<br/>');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t('LUMERAI_SETTINGS.AUTOPILOT_TITLE')"
        :description="t('LUMERAI_SETTINGS.AUTOPILOT_DESCRIPTION')"
        feature-name="lumerai"
      />
    </template>
    <template #body>
      <div
        class="px-8 max-w-7xl mx-auto w-full flex flex-col flex-1 pb-6 relative"
      >
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabArrayIndex"
          full-width
          class="shadow-sm"
          @tab-changed="handleTabChanged"
        />

        <div class="flex-1">
          <!-- Settings -->

          <EventsSettings v-if="activeTabIndex === 21" key="tab-21" />

          <div
            v-if="activeTabIndex === 0"
            key="tab-0"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                {{ t('LUMERAI_SETTINGS.PERSONA.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                {{ t('LUMERAI_SETTINGS.PERSONA.DESCRIPTION') }}
              </p>

              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-n-slate-12"
                    >
                      {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="aiBotGender"
                        class="select-no-native-arrow block w-full h-10 appearance-none px-3 pr-10 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                      >
                        <option value="neutral">
                          {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_NEUTRAL') }}
                        </option>
                        <option value="feminine">
                          {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_FEMININE') }}
                        </option>
                        <option value="masculine">
                          {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_MASCULINE') }}
                        </option>
                        <option value="custom">
                          {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_CUSTOM') }}
                        </option>
                      </select>
                      <i
                        class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-n-slate-10"
                        aria-hidden="true"
                      />
                    </div>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{ t('LUMERAI_SETTINGS.PERSONA.GENDER_HELP') }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-n-slate-12"
                    >
                      {{ t('LUMERAI_SETTINGS.PERSONA.TONE_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="aiResponseTone"
                        class="select-no-native-arrow block w-full h-10 appearance-none px-3 pr-10 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                      >
                        <option value="professional">
                          {{ t('LUMERAI_SETTINGS.PERSONA.TONE_PROFESSIONAL') }}
                        </option>
                        <option value="formal">
                          {{ t('LUMERAI_SETTINGS.PERSONA.TONE_FORMAL') }}
                        </option>
                        <option value="warm">
                          {{ t('LUMERAI_SETTINGS.PERSONA.TONE_WARM') }}
                        </option>
                        <option value="casual">
                          {{ t('LUMERAI_SETTINGS.PERSONA.TONE_CASUAL') }}
                        </option>
                        <option value="direct">
                          {{ t('LUMERAI_SETTINGS.PERSONA.TONE_DIRECT') }}
                        </option>
                      </select>
                      <i
                        class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-n-slate-10"
                        aria-hidden="true"
                      />
                    </div>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{ t('LUMERAI_SETTINGS.PERSONA.TONE_HELP') }}
                    </p>
                  </div>
                </div>

                <div
                  class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] items-center gap-3 md:gap-6 p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
                >
                  <div class="w-full">
                    <label
                      class="mb-1.5 block text-sm font-medium text-n-slate-12"
                      for="lumerai-realtime-voice"
                    >
                      {{ t('LUMERAI_SETTINGS.PERSONA.REALTIME_VOICE_LABEL') }}
                    </label>
                    <p class="text-sm text-n-slate-11 m-0">
                      {{ t('LUMERAI_SETTINGS.PERSONA.REALTIME_VOICE_HELP') }}
                    </p>
                  </div>
                  <div class="relative h-10 w-full">
                    <select
                      id="lumerai-realtime-voice"
                      v-model="lumeraiRealtimeVoice"
                      class="select-no-native-arrow block h-full w-full appearance-none px-3 pr-10 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                    >
                      <option
                        v-for="voice in realtimeVoiceOptions"
                        :key="voice"
                        :value="voice"
                      >
                        {{ voice }}
                      </option>
                    </select>
                    <i
                      class="i-lucide-chevron-down pointer-events-none absolute right-3 top-0 bottom-0 my-auto h-4 w-4 text-base text-n-slate-10"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
                >
                  <div>
                    <h4 class="font-medium text-n-slate-12 m-0">
                      {{ t('LUMERAI_SETTINGS.PERSONA.AUTO_RESOLVE_TITLE') }}
                    </h4>
                    <p class="text-sm text-n-slate-11 m-0">
                      {{
                        t('LUMERAI_SETTINGS.PERSONA.AUTO_RESOLVE_DESCRIPTION')
                      }}
                    </p>
                  </div>
                  <Switch v-model="lumeraiAutoResolveEnabled" />
                </div>

                <div
                  class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_180px] gap-4 items-start p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
                >
                  <div>
                    <h4 class="font-medium text-n-slate-12 m-0">
                      {{ t('LUMERAI_SETTINGS.PERSONA.DELAY_TITLE') }}
                    </h4>
                    <p class="text-sm text-n-slate-11 m-0">
                      {{ t('LUMERAI_SETTINGS.PERSONA.DELAY_DESCRIPTION') }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-n-slate-12"
                      for="lumerai-autopilot-debounce-seconds"
                    >
                      {{ t('LUMERAI_SETTINGS.PERSONA.SECONDS_RANGE') }}
                    </label>
                    <input
                      id="lumerai-autopilot-debounce-seconds"
                      v-model.number="aiAutopilotDebounceSeconds"
                      type="number"
                      min="0"
                      max="30"
                      step="1"
                      class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                      @blur="
                        aiAutopilotDebounceSeconds =
                          normalizeAutopilotDebounceSeconds(
                            aiAutopilotDebounceSeconds
                          )
                      "
                    />
                  </div>
                </div>

                <TextArea
                  v-model="aiPromptPersona"
                  placeholder="Ex: Você é um assistente prestativo. Seja cordial e use emojis."
                  rows="24"
                  custom-text-area-class="min-h-[400px] resize-y"
                  class="font-mono text-sm bg-n-alpha-1 p-2 rounded-lg"
                />

                <div class="flex justify-end mt-4">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTabIndex === 1"
            key="tab-1"
            class="flex flex-col gap-6"
          >
            <!-- Knowledge Base -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                {{ t('LUMERAI_SETTINGS.COMPANY_BASE.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                {{ t('LUMERAI_SETTINGS.COMPANY_BASE.DESCRIPTION') }}
              </p>

              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    v-model="aiBotName"
                    :label="t('LUMERAI_SETTINGS.COMPANY_BASE.BOT_NAME')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.COMPANY_BASE.BOT_NAME_PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="aiCompanyName"
                    :label="t('LUMERAI_SETTINGS.COMPANY_BASE.COMPANY_NAME')"
                    :placeholder="
                      t(
                        'LUMERAI_SETTINGS.COMPANY_BASE.COMPANY_NAME_PLACEHOLDER'
                      )
                    "
                  />
                  <Input
                    v-model="aiCompanyPhone"
                    :label="t('LUMERAI_SETTINGS.COMPANY_BASE.PHONE')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.COMPANY_BASE.PHONE_PLACEHOLDER')
                    "
                    inputmode="tel"
                    autocomplete="tel"
                    @update:model-value="onCompanyPhoneInput"
                  />
                  <Input
                    v-model="aiCompanyEmail"
                    :label="t('LUMERAI_SETTINGS.COMPANY_BASE.EMAIL')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.COMPANY_BASE.EMAIL_PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="aiCompanyHours"
                    :label="t('LUMERAI_SETTINGS.COMPANY_BASE.HOURS')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.COMPANY_BASE.HOURS_PLACEHOLDER')
                    "
                  />
                </div>

                <TextArea
                  v-model="aiCompanyInfo"
                  :label="t('LUMERAI_SETTINGS.COMPANY_BASE.ADDITIONAL_INFO')"
                  :placeholder="
                    t(
                      'LUMERAI_SETTINGS.COMPANY_BASE.ADDITIONAL_INFO_PLACEHOLDER'
                    )
                  "
                  rows="20"
                  custom-text-area-class="min-h-[400px] resize-y"
                  class="font-mono text-sm bg-n-alpha-1 p-2 rounded-lg mt-2"
                />

                <div class="flex justify-end mt-2">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 17"
            key="tab-17"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.PAYMENTS.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.PAYMENTS.DESCRIPTION') }}
                  </p>
                </div>
                <Switch v-model="lumeraiPaymentPolicy.enabled" />
              </div>

              <div
                v-if="lumeraiPaymentPolicy.enabled"
                class="flex flex-col gap-6"
              >
                <div
                  class="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-n-slate-12"
                >
                  <p class="font-semibold m-0 mb-1">
                    Compra online e loja física
                  </p>
                  <p class="m-0">
                    Use esta tabela para compras online. Se a loja física ou
                    balcão tiver condições próprias, a LumerAI exibirá este
                    alerta antes de orientar o cliente.
                  </p>
                  <label class="mt-3 flex items-center gap-2 cursor-pointer">
                    <input
                      v-model="lumeraiPaymentPolicy.online_only_notice"
                      type="checkbox"
                    />
                    Mostrar alerta de que as condições da loja física podem ser
                    diferentes
                  </label>
                </div>

                <div>
                  <h4 class="font-semibold text-n-slate-12 mb-3">
                    Cartão de crédito
                  </h4>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div
                      v-for="(
                        tier, index
                      ) in lumeraiPaymentPolicy.credit_card_installments"
                      :key="index"
                      class="rounded-xl border border-n-strong p-4"
                    >
                      <p class="font-medium text-sm text-n-slate-12 mb-3">
                        Até {{ tier.installments }}x sem juros
                      </p>
                      <Input
                        v-model="tier.minimum_brl"
                        type="number"
                        min="0"
                        step="0.01"
                        label="Valor mínimo (R$)"
                      />
                      <Input
                        v-model="tier.label"
                        class="mt-3"
                        label="Como mostrar ao cliente"
                        placeholder="Ex.: compras acima de R$ 300,00"
                      />
                    </div>
                  </div>
                  <div class="mt-4 max-w-xs">
                    <Input
                      v-model="lumeraiPaymentPolicy.credit_card_analysis_hours"
                      type="number"
                      min="0"
                      label="Prazo máximo de análise do cartão (horas)"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="rounded-xl border border-n-strong p-4">
                    <h4 class="font-semibold text-n-slate-12 mb-3">Boleto</h4>
                    <p class="text-sm text-n-slate-11 mb-4">
                      Não permite parcelamento.
                    </p>
                    <div class="grid grid-cols-2 gap-3">
                      <Input
                        v-model="lumeraiPaymentPolicy.boleto.due_business_days"
                        type="number"
                        min="0"
                        label="Vencimento (dias úteis)"
                      />
                      <Input
                        v-model="
                          lumeraiPaymentPolicy.boleto.compensation_business_days
                        "
                        type="number"
                        min="0"
                        label="Compensação (até dias úteis)"
                      />
                    </div>
                  </div>
                  <div class="rounded-xl border border-n-strong p-4">
                    <h4 class="font-semibold text-n-slate-12 mb-3">PIX</h4>
                    <p class="text-sm text-n-slate-11 mb-4">
                      Não permite parcelamento.
                    </p>
                    <Input
                      v-model="lumeraiPaymentPolicy.pix.expiration_minutes"
                      type="number"
                      min="0"
                      label="Prazo para pagamento (minutos)"
                    />
                    <label
                      class="mt-3 flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
                    >
                      <input
                        v-model="lumeraiPaymentPolicy.pix.quick_approval"
                        type="checkbox"
                      />
                      Informar aprovação em poucos minutos
                    </label>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="rounded-xl border border-dashed border-n-strong bg-n-alpha-1 p-5 text-sm text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.PAYMENTS.EMPTY') }}
              </div>

              <div class="flex justify-end mt-6">
                <Button
                  :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_PAYMENT_POLICY')"
                  color="slate"
                  :is-loading="isUpdating"
                  @click="saveSettings"
                />
              </div>
            </div>
          </div>
          <div v-else-if="activeTabIndex === 11" key="tab-11">
            <KnowledgeBuilder />
          </div>

          <div
            v-else-if="activeTabIndex === 10"
            key="tab-10"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div
                class="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6"
              >
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.KNOWLEDGE.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.KNOWLEDGE.DESCRIPTION') }}
                  </p>
                </div>
                <div
                  v-if="knowledgeDocumentsMeta.limits"
                  class="shrink-0 rounded-lg bg-n-alpha-1 border border-n-strong px-4 py-2 text-sm text-n-slate-11"
                >
                  <strong class="text-n-slate-12">
                    {{ knowledgeDocumentsMeta.limits.consumed || 0 }} /
                    {{ knowledgeDocumentsMeta.limits.total_count || 0 }}
                  </strong>
                  {{ t('LUMERAI_SETTINGS.KNOWLEDGE.INDEXED_PAGES') }}
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-7">
                <div
                  class="p-5 rounded-xl border border-n-strong bg-n-alpha-1 flex flex-col gap-4"
                >
                  <div>
                    <h4 class="font-semibold text-n-slate-12 m-0">
                      {{ t('LUMERAI_SETTINGS.KNOWLEDGE.ADD_URL') }}
                    </h4>
                    <p class="text-xs text-n-slate-11 mt-1 mb-0">
                      {{ t('LUMERAI_SETTINGS.KNOWLEDGE.URL_HELP') }}
                    </p>
                  </div>
                  <Input
                    v-model="knowledgeSourceUrl"
                    :label="t('LUMERAI_SETTINGS.KNOWLEDGE.PAGE_ADDRESS')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.KNOWLEDGE.PAGE_ADDRESS_PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="knowledgeSourceName"
                    :label="t('LUMERAI_SETTINGS.KNOWLEDGE.SOURCE_NAME')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.KNOWLEDGE.SOURCE_NAME_PLACEHOLDER')
                    "
                  />
                  <Button
                    :label="t('LUMERAI_SETTINGS.KNOWLEDGE.ADD_PROCESS_URL')"
                    color="teal"
                    :is-loading="isCreatingKnowledgeDocument"
                    @click="createKnowledgeUrl"
                  />
                </div>

                <div
                  class="p-5 rounded-xl border border-n-strong bg-n-alpha-1 flex flex-col gap-4"
                >
                  <div>
                    <h4 class="font-semibold text-n-slate-12 m-0">
                      {{ t('LUMERAI_SETTINGS.KNOWLEDGE.SEND_PDF') }}
                    </h4>
                    <p class="text-xs text-n-slate-11 mt-1 mb-0">
                      {{ t('LUMERAI_SETTINGS.KNOWLEDGE.PDF_HELP') }}
                    </p>
                  </div>
                  <input
                    ref="knowledgeFileInput"
                    type="file"
                    accept="application/pdf,.pdf"
                    class="block w-full text-sm text-n-slate-11 file:mr-4 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-4 file:py-2 file:text-sm file:font-medium file:text-n-slate-12 hover:file:bg-n-alpha-3"
                    @change="selectKnowledgePdf"
                  />
                  <p
                    v-if="knowledgePdfFile"
                    class="text-sm text-n-slate-12 m-0"
                  >
                    {{ t('LUMERAI_SETTINGS.KNOWLEDGE.SELECTED_FILE') }}:
                    {{ knowledgePdfFile.name }}
                  </p>
                  <Button
                    :label="t('LUMERAI_SETTINGS.KNOWLEDGE.SEND_PROCESS_PDF')"
                    color="teal"
                    :disabled="!knowledgePdfFile"
                    :is-loading="isCreatingKnowledgeDocument"
                    @click="uploadKnowledgePdf"
                  />
                </div>
              </div>

              <div class="flex items-center justify-between mb-3">
                <h4 class="font-semibold text-n-slate-12 m-0">
                  {{ t('LUMERAI_SETTINGS.KNOWLEDGE.REGISTERED_SOURCES') }}
                </h4>
                <Button
                  :label="t('LUMERAI_SETTINGS.ACTIONS.REFRESH')"
                  color="slate"
                  size="sm"
                  :is-loading="isLoadingKnowledgeDocuments"
                  @click="fetchKnowledgeDocuments"
                />
              </div>

              <div
                v-if="isLoadingKnowledgeDocuments && !knowledgeDocuments.length"
                class="text-center py-8 text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.KNOWLEDGE.LOADING') }}
              </div>
              <div
                v-else-if="!knowledgeDocuments.length"
                class="text-center py-8 text-n-slate-11 bg-n-alpha-1 rounded-xl border border-n-strong border-dashed"
              >
                {{ t('LUMERAI_SETTINGS.KNOWLEDGE.EMPTY') }}
              </div>
              <div v-else class="flex flex-col gap-3">
                <div
                  v-for="document in knowledgeDocuments"
                  :key="document.id"
                  class="flex flex-col md:flex-row md:items-center gap-3 justify-between p-4 rounded-xl border border-n-strong bg-n-alpha-1"
                >
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span
                        :class="
                          document.pdf_document
                            ? 'i-lucide-file-text'
                            : 'i-lucide-link'
                        "
                        class="text-n-brand"
                      />
                      <strong class="text-sm text-n-slate-12 truncate">
                        {{ document.name }}
                      </strong>
                    </div>
                    <p class="text-xs text-n-slate-11 mt-1 mb-0 truncate">
                      {{ document.external_link }}
                    </p>
                    <p
                      class="text-xs mt-1 mb-0"
                      :class="
                        document.sync_status === 'failed'
                          ? 'text-n-ruby-9'
                          : 'text-n-slate-11'
                      "
                    >
                      {{ knowledgeDocumentStatus(document) }}
                    </p>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <Button
                      v-if="!document.pdf_document"
                      :label="t('LUMERAI_SETTINGS.ACTIONS.SYNC')"
                      color="slate"
                      size="sm"
                      :disabled="document.status !== 'available'"
                      @click="syncKnowledgeDocument(document)"
                    />
                    <Button
                      :label="t('LUMERAI_SETTINGS.ACTIONS.REMOVE')"
                      color="ruby"
                      size="sm"
                      @click="removeKnowledgeDocument(document)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 2"
            key="tab-2"
            class="flex flex-col gap-6"
          >
            <!-- FAQs -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-lg font-semibold text-n-slate-12">
                  {{ t('LUMERAI_SETTINGS.FAQS.TITLE') }}
                </h3>
                <Button
                  :label="t('LUMERAI_SETTINGS.FAQS.ADD')"
                  color="teal"
                  size="sm"
                  @click="addFaq"
                />
              </div>
              <p class="text-sm text-n-slate-11 mb-6">
                {{ t('LUMERAI_SETTINGS.FAQS.DESCRIPTION') }}
              </p>

              <div class="flex flex-col gap-4">
                <div
                  v-for="(faq, fIdx) in aiFaqs"
                  :key="faq.id"
                  class="p-4 border border-n-strong rounded-xl bg-n-alpha-1 flex flex-col gap-3"
                >
                  <div class="flex justify-between items-center">
                    <h4 class="font-bold text-sm text-n-slate-12">
                      {{ t('LUMERAI_SETTINGS.FAQS.QUESTION') }} #{{ fIdx + 1 }}
                    </h4>
                    <button
                      class="text-red-500 hover:text-red-700 text-xs"
                      @click="removeFaq(fIdx)"
                    >
                      {{ t('LUMERAI_SETTINGS.ACTIONS.REMOVE') }}
                    </button>
                  </div>
                  <Input
                    v-model="faq.question"
                    :placeholder="
                      t('LUMERAI_SETTINGS.FAQS.QUESTION_PLACEHOLDER')
                    "
                  />
                  <TextArea
                    v-model="faq.answer"
                    :placeholder="t('LUMERAI_SETTINGS.FAQS.ANSWER_PLACEHOLDER')"
                    rows="2"
                    class="font-mono text-sm bg-white dark:bg-n-solid-3 p-2 rounded-md border border-n-strong"
                  />
                </div>

                <div
                  v-if="!aiFaqs.length"
                  class="text-center py-6 text-n-slate-11 bg-n-alpha-1 rounded-xl border border-n-strong border-dashed"
                >
                  {{ t('LUMERAI_SETTINGS.FAQS.EMPTY') }}
                </div>

                <div class="flex justify-end mt-2">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 3"
            key="tab-3"
            class="flex flex-col gap-6"
          >
            <!-- Webhooks -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-lg font-semibold text-n-slate-12">
                  {{ t('LUMERAI_SETTINGS.WEBHOOKS.TITLE') }}
                </h3>
                <Button
                  :label="t('LUMERAI_SETTINGS.WEBHOOKS.ADD')"
                  color="teal"
                  size="sm"
                  @click="addWebhook"
                />
              </div>
              <p class="text-sm text-n-slate-11 mb-6">
                {{ t('LUMERAI_SETTINGS.WEBHOOKS.DESCRIPTION') }}
              </p>

              <div class="flex flex-col gap-6">
                <div
                  v-for="(hook, hIdx) in aiWebhooks"
                  :key="hook.id"
                  class="p-5 border border-n-strong rounded-xl bg-n-alpha-1 flex flex-col gap-4"
                >
                  <div class="flex justify-between items-center">
                    <h4 class="font-bold text-n-slate-12">
                      Webhook #{{ hIdx + 1 }}
                    </h4>
                    <button
                      class="text-red-500 hover:text-red-700 text-sm"
                      @click="removeWebhook(hIdx)"
                    >
                      {{ t('LUMERAI_SETTINGS.WEBHOOKS.REMOVE') }}
                    </button>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div class="col-span-4">
                      <Input
                        v-model="hook.name"
                        label="Nome da Ação"
                        placeholder="Ex: consultar_pedido (Sem espaços)"
                      />
                    </div>
                    <div
                      class="col-span-2 flex flex-col min-w-0 gap-1 justify-end"
                    >
                      <label class="mb-0.5 text-heading-3 text-n-slate-12">
                        Método
                      </label>
                      <div class="relative w-full h-10">
                        <select
                          v-model="hook.method"
                          class="block w-full h-10 px-3 py-2.5 pr-8 appearance-none !bg-none outline outline-1 outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 transition-all duration-500 ease-in-out cursor-pointer"
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="PATCH">PATCH</option>
                          <option value="DELETE">DELETE</option>
                        </select>
                        <div
                          class="absolute top-1/2 right-2.5 -translate-y-1/2 pointer-events-none text-n-slate-11"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div class="col-span-6">
                      <Input
                        v-model="hook.url"
                        label="URL do Webhook"
                        placeholder="Ex: https://api.site.com/webhook"
                      />
                    </div>
                  </div>

                  <Input
                    v-model="hook.description"
                    label="Quando usar isso? (Instrução para a IA)"
                    placeholder="Ex: Sempre que o cliente perguntar pelo status do pedido ou solicitar rastreio."
                  />

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Parâmetros -->
                    <div
                      class="mt-2 bg-white dark:bg-n-solid-3 p-4 rounded-lg border border-n-strong"
                    >
                      <div class="flex justify-between items-center mb-3">
                        <p class="font-semibold text-sm text-n-slate-12 m-0">
                          Parâmetros a Extrair
                        </p>
                        <Button
                          label="+ Variável"
                          size="xs"
                          color="slate"
                          @click="addParameter(hIdx)"
                        />
                      </div>
                      <p class="text-xs text-n-slate-11 mb-3">
                        Estes dados serão enviados no Body (POST/PUT) ou na URL
                        (GET).
                      </p>

                      <div class="flex flex-col gap-2">
                        <div
                          v-for="(param, pIdx) in hook.parameters"
                          :key="pIdx"
                          class="flex gap-2 items-start"
                        >
                          <Input
                            v-model="param.name"
                            placeholder="Variável (ex: cpf_cliente)"
                            class="w-1/3"
                          />
                          <Input
                            v-model="param.description"
                            placeholder="Descrição (ex: CPF fornecido)"
                            class="flex-1"
                          />
                          <button
                            class="text-red-500 hover:bg-red-50 p-2 rounded-md"
                            @click="removeParameter(hIdx, pIdx)"
                          >
                            <i class="i-lucide-trash" />
                          </button>
                        </div>
                        <div
                          v-if="!hook.parameters?.length"
                          class="text-xs text-n-slate-10 text-center italic py-2"
                        >
                          Nenhum parâmetro extra.
                        </div>
                      </div>
                    </div>

                    <!-- Cabeçalhos (Headers) -->
                    <div
                      class="mt-2 bg-white dark:bg-n-solid-3 p-4 rounded-lg border border-n-strong"
                    >
                      <div class="flex justify-between items-center mb-3">
                        <p class="font-semibold text-sm text-n-slate-12 m-0">
                          Cabeçalhos (Headers)
                        </p>
                        <Button
                          label="+ Header"
                          size="xs"
                          color="slate"
                          @click="addHeader(hIdx)"
                        />
                      </div>
                      <p class="text-xs text-n-slate-11 mb-3">
                        Defina chaves de API, tokens e content-types estáticos.
                      </p>

                      <div class="flex flex-col gap-2">
                        <div
                          v-for="(header, hdIdx) in hook.headers"
                          :key="hdIdx"
                          class="flex gap-2 items-start"
                        >
                          <Input
                            v-model="header.name"
                            placeholder="Chave (ex: Authorization)"
                            class="w-1/3"
                          />
                          <Input
                            v-model="header.value"
                            placeholder="Valor (ex: Bearer token...)"
                            class="flex-1"
                          />
                          <button
                            class="text-red-500 hover:bg-red-50 p-2 rounded-md"
                            @click="removeHeader(hIdx, hdIdx)"
                          >
                            <i class="i-lucide-trash" />
                          </button>
                        </div>
                        <div
                          v-if="!hook.headers?.length"
                          class="text-xs text-n-slate-10 text-center italic py-2"
                        >
                          Nenhum cabeçalho extra.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!aiWebhooks.length"
                  class="text-center py-8 text-n-slate-11 bg-n-alpha-1 rounded-xl border border-n-strong border-dashed"
                >
                  Nenhuma integração configurada. Clique em "+ Adicionar
                  Webhook".
                </div>

                <div class="flex justify-end mt-4">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_WEBHOOKS')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 14"
            key="tab-14"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.TRANSFERS.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.TRANSFERS.DESCRIPTION') }}
                  </p>
                </div>
                <Button
                  :label="t('LUMERAI_SETTINGS.TRANSFERS.ADD')"
                  color="slate"
                  @click="addTransferRule"
                />
              </div>

              <div
                v-if="!lumeraiTransferRules.length"
                class="text-center py-8 text-n-slate-11 bg-n-alpha-1 rounded-xl border border-n-strong border-dashed"
              >
                {{ t('LUMERAI_SETTINGS.TRANSFERS.EMPTY') }}
              </div>

              <div
                v-for="(rule, index) in lumeraiTransferRules"
                :key="rule.id"
                class="p-4 mb-4 bg-n-alpha-1 border border-n-strong rounded-xl"
              >
                <div class="flex items-center justify-between mb-4">
                  <h4 class="font-medium text-n-slate-12 m-0">
                    Regra {{ index + 1 }}
                  </h4>
                  <button
                    class="text-red-500 hover:bg-red-50 p-2 rounded-md"
                    @click="removeTransferRule(index)"
                  >
                    <i class="i-lucide-trash" />
                  </button>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    v-model="rule.criterion"
                    label="Critério para transferir"
                    placeholder="Ex.: assunto financeiro, cancelar contrato..."
                  />
                  <label class="block text-sm text-n-slate-12">
                    Destino
                    <div class="relative mt-1">
                      <select
                        v-model="rule.target_type"
                        class="select-no-native-arrow block w-full h-10 appearance-none px-3 pr-10 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                      >
                        <option value="team">Equipe</option>
                        <option value="user">Usuário</option>
                      </select>
                      <i
                        class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-n-slate-10"
                        aria-hidden="true"
                      />
                    </div>
                  </label>
                  <label class="block text-sm text-n-slate-12">
                    {{ rule.target_type === 'team' ? 'Equipe' : 'Usuário' }}
                    <div class="relative mt-1">
                      <select
                        v-model="rule.target_id"
                        class="select-no-native-arrow block w-full h-10 appearance-none px-3 pr-10 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                      >
                        <option value="">Selecione</option>
                        <option
                          v-for="target in rule.target_type === 'team'
                            ? transferTeams
                            : transferUsers"
                          :key="target.id"
                          :value="target.id"
                        >
                          {{ target.name || target.available_name }}
                        </option>
                      </select>
                      <i
                        class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-n-slate-10"
                        aria-hidden="true"
                      />
                    </div>
                  </label>
                </div>
              </div>

              <div class="flex justify-end mt-4">
                <Button
                  :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_TRANSFER_RULES')"
                  color="blue"
                  :is-loading="isUpdating"
                  @click="saveSettings"
                />
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 15"
            key="tab-15"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-start gap-3 mb-6">
                <div
                  class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <i class="i-lucide-chart-no-axes-combined text-xl" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.PIPELINE.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.PIPELINE.DESCRIPTION') }}
                  </p>
                </div>
              </div>

              <div
                class="mb-6 flex items-start justify-between gap-4 rounded-xl border border-n-strong bg-n-alpha-1 p-4"
              >
                <div>
                  <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                    {{ t('LUMERAI_SETTINGS.PIPELINE.AUTOMATION_TITLE') }}
                  </h4>
                  <p class="mt-1 mb-0 text-sm text-n-slate-11">
                    {{ t('LUMERAI_SETTINGS.PIPELINE.AUTOMATION_DESCRIPTION') }}
                  </p>
                </div>
                <Switch
                  v-model="lumeraiSalesPipelineAutomationEnabled"
                  @change="saveLumeraiPipelineAutomation"
                />
              </div>

              <template v-if="lumeraiSalesPipelineAutomationEnabled">
                <div
                  class="mb-6 rounded-xl border border-n-strong bg-n-alpha-1 p-4 text-sm text-n-slate-11"
                >
                  <div class="flex gap-2">
                    <i
                      class="i-lucide-shield-check mt-0.5 shrink-0 text-n-teal-10"
                    />
                    <p class="m-0">
                      Por segurança, a LumerAI só pode mover automaticamente uma
                      oportunidade de <strong>Entrada</strong> para
                      <strong>Qualificação</strong>. Situações sem contexto vão
                      para <strong>Revisão necessária</strong>. Proposta,
                      Negociação, Ganho e Perdido permanecem sob decisão humana.
                    </p>
                  </div>
                </div>

                <div
                  v-if="isLoadingLumeraiPipeline"
                  class="py-10 text-center text-sm text-n-slate-11"
                >
                  {{ t('LUMERAI_SETTINGS.PIPELINE.LOADING') }}
                </div>
                <div
                  v-else-if="lumeraiPipelineError"
                  class="rounded-xl border border-n-ruby-7 bg-n-ruby-3/10 p-4 text-sm text-n-ruby-11"
                >
                  {{ lumeraiPipelineError }}
                </div>
                <div
                  v-else-if="!lumeraiPipelineStages.length"
                  class="rounded-xl border border-dashed border-n-strong p-8 text-center text-sm text-n-slate-11"
                >
                  {{ t('LUMERAI_SETTINGS.PIPELINE.EMPTY') }}
                </div>
                <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <article
                    v-for="stage in lumeraiPipelineStages"
                    :key="stage.id"
                    class="rounded-xl border border-n-strong bg-n-alpha-1 p-4"
                  >
                    <div class="mb-3 flex items-center gap-2">
                      <span
                        class="h-3 w-3 shrink-0 rounded-full"
                        :style="{ backgroundColor: stage.color || '#64748B' }"
                      />
                      <h4 class="m-0 text-base font-semibold text-n-slate-12">
                        {{ stage.name }}
                      </h4>
                    </div>
                    <TextArea
                      v-model="stage.lumerai_instruction"
                      :label="`Como a LumerAI deve agir em ${stage.name}`"
                      :placeholder="defaultPipelineInstruction(stage)"
                      :max-length="2000"
                      show-character-count
                      auto-height
                      min-height="6rem"
                    />
                  </article>
                </div>

                <div
                  v-if="lumeraiPipelineStages.length"
                  class="mt-6 flex justify-end"
                >
                  <Button
                    :label="
                      t('LUMERAI_SETTINGS.ACTIONS.SAVE_PIPELINE_GUIDANCE')
                    "
                    color="blue"
                    :is-loading="isSavingLumeraiPipeline"
                    @click="saveLumeraiPipelineGuidance"
                  />
                </div>
              </template>
              <div
                v-else
                class="rounded-xl border border-dashed border-n-strong bg-n-alpha-1 p-6 text-center text-sm text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.PIPELINE.DISABLED') }}
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 16"
            key="tab-16"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-start gap-3 mb-6">
                <div
                  class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <i class="i-lucide-calendar-clock text-xl" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.CALENDARS.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.CALENDARS.DESCRIPTION') }}
                  </p>
                </div>
              </div>
              <div
                class="mb-6 rounded-xl border border-n-strong bg-n-alpha-1 p-4 text-sm text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.CALENDARS.NOTICE') }}
              </div>
              <div
                v-if="isLoadingLumeraiCalendars"
                class="py-10 text-center text-sm text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.CALENDARS.LOADING') }}
              </div>
              <div
                v-else-if="!lumeraiCalendars.length"
                class="rounded-xl border border-dashed border-n-strong p-8 text-center text-sm text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.CALENDARS.EMPTY') }}
              </div>
              <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <article
                  v-for="calendar in lumeraiCalendars"
                  :key="calendar.id"
                  class="rounded-xl border border-n-strong bg-n-alpha-1 p-4"
                >
                  <div class="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h4 class="m-0 text-base font-semibold text-n-slate-12">
                        {{ calendar.name }}
                      </h4>
                      <p
                        v-if="calendar.description"
                        class="mt-1 mb-0 text-xs text-n-slate-11"
                      >
                        {{ calendar.description }}
                      </p>
                    </div>
                    <span class="text-xs text-n-slate-11"
                      >{{ calendar.agents?.length || 0 }} agente(s)</span
                    >
                  </div>
                  <TextArea
                    v-model="calendar.lumerai_instruction"
                    label="Como a LumerAI deve agir neste calendário"
                    :placeholder="defaultCalendarInstruction(calendar)"
                    :max-length="2000"
                    show-character-count
                    auto-height
                    min-height="6rem"
                  />
                  <div class="mt-4 flex justify-end">
                    <Button
                      :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_GUIDANCE')"
                      color="blue"
                      :is-loading="isSavingLumeraiCalendar === calendar.id"
                      @click="saveLumeraiCalendarInstruction(calendar)"
                    />
                  </div>
                </article>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 12"
            key="tab-12"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <div class="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    {{ t('LUMERAI_SETTINGS.STICKERS.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-slate-11 m-0">
                    {{ t('LUMERAI_SETTINGS.STICKERS.DESCRIPTION') }}
                  </p>
                </div>
                <Switch
                  v-model="aiStickersEnabled"
                  :disabled="isSavingStickerPermissions"
                  @update:model-value="saveStickerPermissions"
                />
              </div>

              <div
                class="rounded-xl border p-4 mb-6"
                :class="[
                  aiStickersEnabled
                    ? 'border-teal-500/40 bg-teal-500/5'
                    : 'border-n-strong bg-n-alpha-1',
                ]"
              >
                <p class="font-medium text-n-slate-12 m-0">
                  {{
                    aiStickersEnabled
                      ? t('LUMERAI_SETTINGS.STICKERS.ENABLED')
                      : t('LUMERAI_SETTINGS.STICKERS.DISABLED')
                  }}
                </p>
                <p class="text-sm text-n-slate-11 mt-1 mb-0">
                  {{ t('LUMERAI_SETTINGS.STICKERS.LIMIT') }}
                </p>
              </div>

              <template v-if="aiStickersEnabled">
                <div
                  class="p-5 rounded-xl border border-n-strong bg-n-alpha-1 mb-6"
                >
                  <h4 class="font-semibold text-n-slate-12 m-0 mb-4">
                    {{ t('LUMERAI_SETTINGS.STICKERS.ADD_TITLE') }}
                  </h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      v-model="stickerName"
                      :label="t('LUMERAI_SETTINGS.STICKERS.NAME')"
                      :placeholder="
                        t('LUMERAI_SETTINGS.STICKERS.NAME_PLACEHOLDER')
                      "
                    />
                    <div>
                      <label
                        class="mb-1.5 block text-sm font-medium text-n-slate-12"
                      >
                        {{ t('LUMERAI_SETTINGS.STICKERS.FILE') }}
                      </label>
                      <input
                        ref="stickerFileInput"
                        type="file"
                        accept="image/webp,.webp"
                        class="block w-full text-sm text-n-slate-11 file:mr-4 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-4 file:py-2 file:text-sm file:font-medium file:text-n-slate-12 hover:file:bg-n-alpha-3"
                        @change="onStickerFileChange"
                      />
                      <p class="mt-1 text-xs text-n-slate-11">
                        {{ t('LUMERAI_SETTINGS.STICKERS.FILE_HELP') }}
                      </p>
                    </div>
                  </div>
                  <TextArea
                    v-model="stickerInstruction"
                    :label="t('LUMERAI_SETTINGS.STICKERS.INSTRUCTION')"
                    :placeholder="
                      t('LUMERAI_SETTINGS.STICKERS.INSTRUCTION_PLACEHOLDER')
                    "
                    rows="3"
                    class="mt-4"
                  />
                  <div class="flex justify-end mt-4">
                    <Button
                      :label="t('LUMERAI_SETTINGS.STICKERS.ADD')"
                      color="teal"
                      :is-loading="isSavingSticker"
                      @click="createLumeraiSticker"
                    />
                  </div>
                </div>

                <div
                  v-if="isLoadingStickers"
                  class="py-8 text-center text-sm text-n-slate-11"
                >
                  {{ t('LUMERAI_SETTINGS.STICKERS.LOADING') }}
                </div>
                <div
                  v-else-if="!lumeraiStickers.length"
                  class="py-8 text-center text-sm text-n-slate-11 rounded-xl border border-dashed border-n-strong"
                >
                  {{ t('LUMERAI_SETTINGS.STICKERS.EMPTY') }}
                </div>
                <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <article
                    v-for="sticker in lumeraiStickers"
                    :key="sticker.id"
                    class="flex gap-4 p-4 rounded-xl border border-n-strong bg-n-alpha-1"
                  >
                    <img
                      :src="sticker.file_url"
                      :alt="sticker.name"
                      class="w-20 h-20 object-contain rounded-lg bg-n-solid-2 shrink-0"
                    />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center justify-between gap-2">
                        <h4 class="font-semibold text-n-slate-12 truncate m-0">
                          {{ sticker.name }}
                        </h4>
                        <button
                          class="text-xs font-medium text-red-500 hover:text-red-600"
                          @click="removeLumeraiSticker(sticker)"
                        >
                          {{ t('LUMERAI_SETTINGS.ACTIONS.REMOVE') }}
                        </button>
                      </div>
                      <p class="text-sm text-n-slate-11 mt-2 mb-3">
                        {{ sticker.instruction }}
                      </p>
                      <button
                        class="text-xs font-medium rounded-md px-2.5 py-1.5"
                        :class="[
                          sticker.active
                            ? 'bg-teal-500/15 text-teal-600 dark:text-teal-400'
                            : 'bg-n-alpha-2 text-n-slate-11',
                        ]"
                        @click="toggleLumeraiSticker(sticker)"
                      >
                        {{
                          sticker.active
                            ? t('LUMERAI_SETTINGS.STICKERS.AUTHORIZED')
                            : t('LUMERAI_SETTINGS.STICKERS.DISABLED')
                        }}
                      </button>
                    </div>
                  </article>
                </div>

                <div class="flex justify-end mt-6">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_OTHER_SETTINGS')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </template>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 4"
            key="tab-4"
            class="flex flex-col gap-6"
          >
            <!-- Inboxes -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                {{ t('LUMERAI_SETTINGS.INBOXES.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                {{ t('LUMERAI_SETTINGS.INBOXES.DESCRIPTION') }}
              </p>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  class="flex items-center justify-between p-4 bg-n-alpha-1 border border-n-strong rounded-xl hover:bg-n-alpha-2 transition-colors"
                >
                  <div class="flex items-center gap-3">
                    <img
                      v-if="inbox.avatar_url"
                      :src="inbox.avatar_url"
                      class="w-8 h-8 rounded-full shadow-sm"
                    />
                    <div
                      v-else
                      class="w-8 h-8 rounded-full bg-n-brand flex items-center justify-center text-white font-bold"
                    >
                      {{ inbox.name.charAt(0) }}
                    </div>
                    <div>
                      <p class="font-medium text-n-slate-12 m-0">
                        {{ inbox.name }}
                      </p>
                      <p class="text-xs text-n-slate-10 m-0">
                        {{ inbox.channel_type }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <Switch
                      :model-value="
                        aiAutopilotInboxes
                          .map(Number)
                          .includes(Number(inbox.id))
                      "
                      @update:model-value="toggleInbox(inbox.id)"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="!inboxes.length"
                class="text-center py-8 text-n-slate-11"
              >
                {{ t('LUMERAI_SETTINGS.INBOXES.EMPTY') }}
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 20"
            key="tab-20"
            class="flex flex-col gap-6"
          >
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                Atendimento de ligações pela LumerAI
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                Quando ativado, a LumerAI atende chamadas recebidas do WhatsApp
                nesta caixa usando a persona e o fluxo ativo da conta. A IA só
                assume chamadas sem responsável, de conversas novas ou sem
                atividade do cliente há mais de 24 horas.
              </p>

              <div
                class="mb-6 rounded-lg border border-n-brand/30 bg-n-brand/5 p-4 text-sm text-n-slate-11"
              >
                Para funcionar, a caixa precisa usar WhatsApp Cloud com chamadas
                habilitadas na Meta e a chave da LumerAI precisa ter acesso à
                API Realtime. Se a IA estiver desativada ou não conseguir
                negociar a chamada, o toque normal para a equipe continua.
              </div>

              <div
                class="mb-6 flex items-center justify-between gap-4 rounded-xl border border-n-strong bg-n-alpha-1 p-4"
              >
                <div>
                  <p class="m-0 font-medium text-n-slate-12">
                    Salvar transcrição da ligação
                  </p>
                  <p class="m-0 mt-1 text-sm text-n-slate-11">
                    Desativado por padrão. Quando ativado, as falas reconhecidas
                    da pessoa e da LumerAI ficam registradas na chamada.
                  </p>
                </div>
                <Switch
                  v-model="lumeraiVoiceTranscriptsEnabled"
                  aria-label="Salvar transcrições das ligações da LumerAI"
                  @update:model-value="toggleVoiceTranscriptCapture"
                />
              </div>

              <div
                v-if="whatsappVoiceInboxes.length"
                class="grid grid-cols-1 gap-4"
                :class="{
                  'md:grid-cols-2': whatsappVoiceInboxes.length > 1,
                }"
              >
                <div
                  v-for="inbox in whatsappVoiceInboxes"
                  :key="inbox.id"
                  class="flex flex-col gap-4 p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
                >
                  <div class="flex items-center justify-between gap-4">
                    <div class="flex items-center gap-3 min-w-0">
                      <img
                        v-if="inbox.avatar_url"
                        :src="inbox.avatar_url"
                        class="w-8 h-8 rounded-full shadow-sm"
                      />
                      <div
                        v-else
                        class="w-8 h-8 shrink-0 rounded-full bg-n-brand flex items-center justify-center text-white font-bold"
                      >
                        {{ inbox.name.charAt(0) }}
                      </div>
                      <div class="min-w-0">
                        <p class="font-medium text-n-slate-12 m-0 truncate">
                          {{ inbox.name }}
                        </p>
                        <p class="text-xs text-n-slate-10 m-0">WhatsApp</p>
                      </div>
                    </div>
                    <Switch
                      :model-value="
                        lumeraiVoiceCallInboxes.includes(Number(inbox.id))
                      "
                      :aria-label="`Ativar LumerAI em ligações: ${inbox.name}`"
                      @update:model-value="toggleVoiceCallInbox(inbox.id)"
                    />
                  </div>

                  <div
                    v-if="lumeraiVoiceCallInboxes.includes(Number(inbox.id))"
                    class="border-t border-n-strong pt-4"
                  >
                    <div class="mb-3">
                      <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                        O que a LumerAI pode fazer na ligação?
                      </h4>
                      <p class="m-0 mt-1 text-xs text-n-slate-11">
                        Por padrão, herda as ferramentas ativas no chat.
                        Desative aqui qualquer capacidade que não deve ser usada
                        por voz.
                      </p>
                    </div>
                    <div class="grid grid-cols-1 gap-2">
                      <div
                        v-for="option in voiceCallCapabilityOptions"
                        :key="option.key"
                        class="flex items-center justify-between gap-4 rounded-lg border border-n-strong bg-n-solid-1 p-3"
                      >
                        <div class="min-w-0">
                          <p class="m-0 text-sm font-medium text-n-slate-12">
                            {{ option.label }}
                          </p>
                          <p class="m-0 mt-1 text-xs text-n-slate-11">
                            {{ option.description }}
                          </p>
                        </div>
                        <Switch
                          :model-value="
                            getVoiceCallCapabilities(inbox.id).includes(
                              option.key
                            )
                          "
                          :disabled="
                            savingVoiceCallCapabilities.includes(
                              Number(inbox.id)
                            )
                          "
                          :aria-label="`${option.label} nas ligações de ${inbox.name}`"
                          @update:model-value="
                            toggleVoiceCallCapability(inbox.id, option.key)
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-8 text-n-slate-11">
                Nenhuma caixa de entrada do WhatsApp disponível nesta conta.
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 5"
            key="tab-5"
            class="flex flex-col gap-6 h-[calc(100vh-200px)]"
          >
            <!-- Playground -->
            <div
              class="bg-white dark:bg-n-solid-2 flex flex-col rounded-xl shadow-sm border border-n-strong h-full overflow-hidden"
            >
              <div
                class="p-4 bg-n-alpha-1 border-b border-n-strong flex items-center justify-between gap-3"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white"
                  >
                    <i class="i-lucide-bot text-xl" />
                  </div>
                  <div>
                    <h3 class="text-md font-semibold text-n-slate-12 m-0">
                      LumerAI Playground
                    </h3>
                    <p class="text-xs text-n-slate-11 m-0">
                      {{ t('LUMERAI_SETTINGS.PLAYGROUND.DESCRIPTION') }}
                    </p>
                  </div>
                </div>
                <Button
                  :label="t('LUMERAI_SETTINGS.PLAYGROUND.CLEAR')"
                  color="slate"
                  size="xs"
                  icon="i-lucide-trash"
                  @click="clearPlayground"
                />
                <div class="flex flex-wrap items-center justify-end gap-2">
                  <span
                    v-if="voiceCallActive"
                    class="text-xs text-n-slate-11"
                    aria-live="polite"
                  >
                    {{ voiceCallStatusLabel }}
                  </span>
                  <Button
                    :label="voiceCallButtonLabel"
                    :color="voiceCallActive ? 'ruby' : 'blue'"
                    size="xs"
                    :icon="
                      voiceCallActive ? 'i-lucide-phone-off' : 'i-lucide-phone'
                    "
                    @click="toggleVoicePlayground"
                  />
                </div>
              </div>

              <div
                v-if="shopifyIntegrationAvailable"
                class="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 py-3 border-b border-n-strong bg-white dark:bg-n-solid-2"
              >
                <Input
                  v-model="playgroundContactPhone"
                  type="tel"
                  :label="t('LUMERAI_SETTINGS.PLAYGROUND.CONTACT_PHONE')"
                  :placeholder="
                    t('LUMERAI_SETTINGS.PLAYGROUND.CONTACT_PHONE_PLACEHOLDER')
                  "
                />
                <Input
                  v-model="playgroundContactEmail"
                  type="email"
                  :label="t('LUMERAI_SETTINGS.PLAYGROUND.CONTACT_EMAIL')"
                  :placeholder="
                    t('LUMERAI_SETTINGS.PLAYGROUND.CONTACT_EMAIL_PLACEHOLDER')
                  "
                />
                <p class="md:col-span-2 text-xs text-n-slate-11 m-0">
                  {{ t('LUMERAI_SETTINGS.PLAYGROUND.CONTACT_HELP') }}
                </p>
              </div>

              <div
                class="flex-1 p-6 overflow-y-auto flex flex-col gap-4 bg-n-alpha-1"
              >
                <div
                  v-for="(msg, idx) in playgroundMessages"
                  :key="idx"
                  class="flex"
                  :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
                >
                  <div
                    class="max-w-[70%] p-3 rounded-2xl shadow-sm overflow-hidden"
                    :class="
                      msg.role === 'user'
                        ? 'bg-n-brand text-white rounded-tr-sm'
                        : 'bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-tl-sm border border-n-strong'
                    "
                  >
                    <div
                      v-if="msg.content"
                      v-html="formatMessage(msg.content)"
                    />
                    <div
                      v-if="msg.stickers?.length"
                      class="flex flex-wrap gap-2 mt-2"
                    >
                      <img
                        v-for="sticker in msg.stickers"
                        :key="sticker.id"
                        :src="sticker.file_url"
                        :alt="sticker.name"
                        class="w-24 h-24 object-contain rounded-lg"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="isSimulating" class="flex justify-start">
                  <div
                    class="bg-white dark:bg-n-solid-3 text-n-slate-11 p-3 rounded-2xl rounded-tl-sm shadow-sm border border-n-strong flex gap-1 items-center"
                  >
                    <span
                      class="w-2 h-2 bg-n-slate-9 rounded-full animate-bounce"
                    />
                    <span
                      class="w-2 h-2 bg-n-slate-9 rounded-full animate-bounce [animation-delay:0.1s]"
                    />
                    <span
                      class="w-2 h-2 bg-n-slate-9 rounded-full animate-bounce [animation-delay:0.2s]"
                    />
                  </div>
                </div>
              </div>

              <!-- Input Area -->
              <div
                class="p-4 border-t border-n-strong bg-white dark:bg-n-solid-2"
              >
                <p class="mb-2 text-xs text-n-slate-10">
                  {{ t('LUMERAI_SETTINGS.PLAYGROUND.VOICE_HELP') }}
                </p>
                <form
                  class="flex gap-2"
                  @submit.prevent="sendPlaygroundMessage()"
                >
                  <TextArea
                    v-model="playgroundInput"
                    placeholder="Digite sua mensagem para testar a IA..."
                    class="flex-1 bg-n-alpha-1 font-mono text-sm"
                    rows="3"
                    @keydown.enter.prevent="sendPlaygroundMessage()"
                  />
                  <button
                    type="submit"
                    :disabled="!playgroundInput.trim() || isSimulating"
                    class="w-10 h-10 rounded-full bg-n-brand text-white flex items-center justify-center hover:bg-n-brand-dark transition-colors disabled:opacity-50"
                  >
                    <i class="i-lucide-send text-sm" />
                  </button>
                </form>
              </div>
            </div>
          </div>
          <SgpIntegrationSettings
            v-else-if="activeTabIndex === 6"
            key="tab-6"
            v-model:usage="sgpUsage"
            v-model:action-instructions="sgpActionInstructions"
            :action-policy-items="sgpActionPolicyItems"
            :is-saving="isSavingSgpUsage"
            @save="saveSgpUsage"
          />
          <AbmIntegrationSettings
            v-else-if="activeTabIndex === 13"
            key="tab-13"
            v-model:catalog-enabled="abmCatalogEnabled"
            v-model:catalog-result-limit="abmCatalogResultLimit"
            v-model:catalog-images-only="abmCatalogImagesOnly"
            v-model:catalog-usage-instruction="abmCatalogUsageInstruction"
            v-model:client-lookup-usage-instruction="
              abmClientLookupUsageInstruction
            "
            :is-saving="isSavingAbmUsage"
            @save="saveAbmUsageInstructions"
          />
          <ShopifyIntegrationSettings
            v-else-if="activeTabIndex === 18"
            key="tab-18"
            v-model:policy="shopifyPolicy"
            v-model:action-instructions="shopifyActionInstructions"
            v-model:store-name="shopifyStoreName"
            v-model:catalog-result-limit="shopifyCatalogResultLimit"
            v-model:catalog-images-only="shopifyCatalogImagesOnly"
            v-model:meta-catalog-enabled="shopifyMetaCatalogEnabled"
            v-model:meta-catalog-id="shopifyMetaCatalogId"
            v-model:meta-catalog-token="shopifyMetaCatalogToken"
            :action-policy-items="shopifyActionPolicyItems"
            :meta-catalog-token-configured="shopifyMetaCatalogTokenConfigured"
            :is-saving="isSavingShopifyUsage"
            @save="saveShopifyUsage"
          />
          <SiprovIntegrationSettings
            v-else-if="activeTabIndex === 19"
            key="tab-19"
            v-model:usage="siprovUsage"
            :action-policy-items="siprovActionPolicyItems"
            :is-saving="isSavingSiprovUsage"
            @save="saveSiprovUsage"
          />
          <div
            v-else-if="activeTabIndex === 7"
            key="tab-7"
            class="flex flex-col gap-6"
          >
            <!-- IXC Integration -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                Integração IXC Soft
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                Configure as credenciais do Webservice IXC para que a LumerAI
                possa consultar segundas vias e realizar desbloqueios em
                confiança diretamente pelo WhatsApp.
              </p>

              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    v-model="ixcUrl"
                    label="IXC URL Base"
                    placeholder="Ex: https://provedor.ixcsoft.com.br"
                  />
                  <Input
                    v-model="ixcToken"
                    label="IXC Token"
                    placeholder="Token do Webservice IXC"
                    type="password"
                  />
                </div>

                <div class="flex justify-end mt-4">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 8"
            key="tab-8"
            class="flex flex-col gap-6"
          >
            <!-- MK-Auth Integration -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                Integração MK-Auth
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                Configure as credenciais da API do MK-Auth para que a LumerAI
                possa consultar segundas vias e realizar desbloqueios em
                confiança diretamente pelo WhatsApp.
              </p>

              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    v-model="mkauthUrl"
                    label="MK-Auth URL Base"
                    placeholder="Ex: https://mkauth.provedor.com.br"
                  />
                  <Input
                    v-model="mkauthClientId"
                    label="Client ID"
                    placeholder="Client ID da API MK-Auth"
                  />
                  <Input
                    v-model="mkauthClientSecret"
                    label="Client Secret"
                    placeholder="Client Secret da API MK-Auth"
                    type="password"
                  />
                </div>

                <div class="flex justify-end mt-4">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="activeTabIndex === 9"
            key="tab-9"
            class="flex flex-col gap-6"
          >
            <!-- HubSoft Integration -->
            <div
              class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
            >
              <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                Integração HubSoft
              </h3>
              <p class="text-sm text-n-slate-11 mb-6">
                Configure as credenciais da API do HubSoft para consultar
                segundas vias e realizar outras integrações diretamente pelo
                WhatsApp.
              </p>

              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    v-model="hubsoftUrl"
                    label="HubSoft URL Base"
                    placeholder="Ex: https://api.hubsoft.com.br"
                  />
                  <Input
                    v-model="hubsoftToken"
                    label="Token (Bearer)"
                    placeholder="Insira o Access Token"
                    type="password"
                  />
                </div>

                <div class="flex justify-end mt-4">
                  <Button
                    :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_CHANGES')"
                    color="slate"
                    :is-loading="isUpdating"
                    @click="saveSettings"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="confirmTitle"
      :description="confirmDescription"
    />
  </SettingsLayout>
</template>

<style scoped>
.select-no-native-arrow {
  appearance: none;
  -webkit-appearance: none;
  background-image: none;
}

.select-no-native-arrow::-ms-expand {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
